import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Film, Tags, Users, Plus, Pencil, Trash2, X, ArrowLeft, BarChart3, Download, Loader2, Clapperboard, Search, Upload, Lock, Megaphone, MessageSquare, Image as ImageIcon, Star, Check, BookOpen, XCircle, Eye, Globe, FileSpreadsheet, Database, Mail, Send, CheckCircle2, Server, UserCheck, RefreshCw, SendHorizontal, AlertCircle, Scale, ShieldAlert, Radio, Activity, Key, KeyRound, Copy, Ban, LogOut, ShieldCheck, Sparkles, HardDrive, Tv, ExternalLink } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { FestivalsAdminTab } from "@/components/FestivalsAdminTab";
import { LiveAdminTab } from "@/components/LiveAdminTab";
import { ImageInputUploader } from "@/components/ImageInputUploader";
import { useAuth } from "@/context/AuthContext";
import { STREAMING_PLATFORMS, findPlatformInfo } from "@/constants/platforms";
import { PlatformLogo } from "@/components/StreamingPlatforms";

const EMPTY_MOVIE = {
  title: "", description: "", year: "", duration: "", category_ids: [], genres: [],
  poster_url: "", backdrop_url: "", trailer_url: "", cast: [], director: "", featured: false,
  upcoming: false, release_date: "", custom_badge: "", platforms: [],
};

export default function Admin() {
  const { user } = useAuth();
  const [tab, setTab] = useState("live");
  const [stats, setStats] = useState(null);
  const [movies, setMovies] = useState([]);
  const [moviesTotal, setMoviesTotal] = useState(0);
  const [movieSkip, setMovieSkip] = useState(0);
  const [movieSearch, setMovieSearch] = useState("");
  const [categories, setCategories] = useState([]);
  const [users, setUsers] = useState([]);
  const [usersTotal, setUsersTotal] = useState(0);
  const [userSearch, setUserSearch] = useState("");
  const [userSkip, setUserSkip] = useState(0);
  const [userProvider, setUserProvider] = useState("all");
  const [userCountry, setUserCountry] = useState("");
  const [userDateFrom, setUserDateFrom] = useState("");
  const [userDateTo, setUserDateTo] = useState("");
  const [countries, setCountries] = useState([]);
  const [exporting, setExporting] = useState(false);
  const [importingSubs, setImportingSubs] = useState(false);
  const [importingMovies, setImportingMovies] = useState(false);
  const [importUrl, setImportUrl] = useState("");
  const [showImportModal, setShowImportModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [catName, setCatName] = useState("");
  const [imp, setImp] = useState(null);
  const [impForm, setImpForm] = useState({ movies_pages: 60, tv_pages: 48, media: "all" });
  const [adsForm, setAdsForm] = useState({ preroll_text: "", skip_seconds: 5, enabled: true });
  const [heroId, setHeroId] = useState(null);
  const [tmdbQuery, setTmdbQuery] = useState("");
  const [tmdbResults, setTmdbResults] = useState(null);
  const [tmdbSearching, setTmdbSearching] = useState(false);
  const [tmdbType, setTmdbType] = useState("title");
  const [tmdbMeta, setTmdbMeta] = useState({ page: 1, total_pages: 0, total_results: 0, person_name: null });
  const [addingKey, setAddingKey] = useState(null);
  const [commentsList, setCommentsList] = useState([]);
  const [commentsTotal, setCommentsTotal] = useState(0);
  const [commentsSkip, setCommentsSkip] = useState(0);
  const [adminArticles, setAdminArticles] = useState([]);
  const [adminArticleFilter, setAdminArticleFilter] = useState("");

  // Newsletter & Amazon SES State
  const [newsletterSubTab, setNewsletterSubTab] = useState("campaigns"); // "campaigns", "welcome", "ses", "subscribers", "logs"
  const [sesConfig, setSesConfig] = useState({ region: "us-east-1", accessKeyId: "", secretAccessKey: "", fromEmail: "boletin@lacajalgbt.com", fromName: "La Caja LGBT", status: "simulation_mode" });
  const [welcomeConfig, setWelcomeConfig] = useState({ enabled: true, subject: "", html: "" });
  const [campaignsList, setCampaignsList] = useState([]);
  const [subscribersList, setSubscribersList] = useState([]);
  const [emailLogsList, setEmailLogsList] = useState([]);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [testingEmail, setTestingEmail] = useState("");
  const [sendingTest, setSendingTest] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);
  const [sendingCampaignId, setSendingCampaignId] = useState(null);
  const [savingSes, setSavingSes] = useState(false);
  const [savingWelcome, setSavingWelcome] = useState(false);

  // DMCA & Copyright Claims State
  const [dmcaClaims, setDmcaClaims] = useState([]);
  const [dmcaFilter, setDmcaFilter] = useState("all");
  const [loadingDmca, setLoadingDmca] = useState(false);
  const [selectedDmca, setSelectedDmca] = useState(null);
  const [dmcaNote, setDmcaNote] = useState("");

  const loadDmcaClaims = useCallback(async () => {
    setLoadingDmca(true);
    try {
      const { data } = await api.get("/admin/dmca-claims");
      setDmcaClaims(Array.isArray(data) ? data : []);
    } catch (err) {
      console.warn("Error loading DMCA claims:", err);
    } finally {
      setLoadingDmca(false);
    }
  }, []);

  const loadNewsletterData = useCallback(async () => {
    try {
      const [cRes, campRes, wRes, subRes, logRes] = await Promise.all([
        api.get("/admin/newsletter/config"),
        api.get("/admin/newsletter/campaigns"),
        api.get("/admin/newsletter/welcome"),
        api.get("/admin/newsletter/subscribers"),
        api.get("/admin/newsletter/logs")
      ]);
      setSesConfig(cRes.data || {});
      setCampaignsList(campRes.data || []);
      setWelcomeConfig(wRes.data || { enabled: true, subject: "", html: "" });
      setSubscribersList(subRes.data || []);
      setEmailLogsList(logRes.data || []);
    } catch (err) {
      console.warn("Newsletter data fetch error:", err);
    }
  }, []);

  useEffect(() => {
    if (tab === "newsletter") {
      loadNewsletterData();
    }
    if (tab === "dmca") {
      loadDmcaClaims();
    }
  }, [tab, loadNewsletterData, loadDmcaClaims]);

  const updateDmcaStatus = async (claimId, status, notes = "", deleteMovieId = null) => {
    try {
      await api.put(`/admin/dmca-claims/${claimId}`, { status, admin_notes: notes, delete_movie_id: deleteMovieId });
      toast.success("Expediente DMCA actualizado correctamente");
      loadDmcaClaims();
      if (selectedDmca && selectedDmca.id === claimId) {
        setSelectedDmca(prev => ({ ...prev, status, admin_notes: notes }));
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al actualizar expediente");
    }
  };

  const deleteDmcaClaim = async (claimId) => {
    if (!window.confirm("¿Seguro que deseas eliminar este registro de expediente DMCA?")) return;
    try {
      await api.delete(`/admin/dmca-claims/${claimId}`);
      toast.success("Expediente eliminado");
      setSelectedDmca(null);
      loadDmcaClaims();
    } catch (err) {
      toast.error("Error al eliminar expediente");
    }
  };

  const handleSaveSes = async (e) => {
    e.preventDefault();
    setSavingSes(true);
    try {
      const { data } = await api.post("/admin/newsletter/config", sesConfig);
      toast.success(data.message || "Configuración de Amazon SES guardada");
      loadNewsletterData();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingSes(false);
    }
  };

  const handleTestSesConnection = async () => {
    if (!testingEmail || !testingEmail.includes("@")) {
      toast.error("Por favor escribe un correo de prueba válido");
      return;
    }
    setTestingConnection(true);
    try {
      const { data } = await api.post("/admin/newsletter/test-connection", { target_email: testingEmail });
      toast.success(data.message);
      loadNewsletterData();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setTestingConnection(false);
    }
  };

  const handleSaveWelcome = async (e) => {
    e.preventDefault();
    setSavingWelcome(true);
    try {
      const { data } = await api.post("/admin/newsletter/welcome", welcomeConfig);
      toast.success(data.message || "Plantilla de bienvenida guardada");
      loadNewsletterData();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingWelcome(false);
    }
  };

  const handleSaveCampaign = async (e) => {
    e.preventDefault();
    if (!editingCampaign?.title || !editingCampaign?.subject) {
      toast.error("El título interno y el asunto del correo son requeridos");
      return;
    }
    try {
      await api.post("/admin/newsletter/campaigns", editingCampaign);
      toast.success("Campaña guardada con éxito");
      setEditingCampaign(null);
      loadNewsletterData();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const handleTestSendCampaign = async (campaignId) => {
    if (!testingEmail || !testingEmail.includes("@")) {
      toast.error("Ingresa un correo de prueba válido en la barra superior");
      return;
    }
    setSendingTest(true);
    try {
      const { data } = await api.post(`/admin/newsletter/campaigns/${campaignId}/test-send`, { target_email: testingEmail });
      toast.success(data.message);
      loadNewsletterData();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSendingTest(false);
    }
  };

  const handleSendCampaign = async (campaignId) => {
    if (!window.confirm("¿Confirmas enviar esta campaña a todos los destinatarios activos mediante Amazon SES?")) return;
    setSendingCampaignId(campaignId);
    try {
      const { data } = await api.post(`/admin/newsletter/campaigns/${campaignId}/send`);
      toast.success(data.message);
      loadNewsletterData();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSendingCampaignId(null);
    }
  };

  const handleDeleteCampaign = async (campaignId) => {
    if (!window.confirm("¿Estás seguro de eliminar esta campaña?")) return;
    try {
      await api.delete(`/admin/newsletter/campaigns/${campaignId}`);
      toast.success("Campaña eliminada");
      loadNewsletterData();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const loadAdminArticles = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/articles", { params: adminArticleFilter ? { status: adminArticleFilter } : {} });
      setAdminArticles(data.articles || []);
    } catch {
      setAdminArticles([]);
    }
  }, [adminArticleFilter]);

  const loadAds = useCallback(async () => {
    const { data } = await api.get("/admin/ads");
    setAdsForm({ preroll_text: (data.preroll_urls || []).join("\n"), skip_seconds: data.skip_seconds ?? 5, enabled: data.enabled ?? true });
  }, []);

  const saveAds = async () => {
    const urls = adsForm.preroll_text.split("\n").map((s) => s.trim()).filter(Boolean);
    try {
      await api.put("/admin/ads", { preroll_urls: urls, skip_seconds: parseInt(adsForm.skip_seconds) || 5, enabled: adsForm.enabled });
      toast.success("Publicidad en video guardada");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const loadUsers = useCallback(async () => {
    const { data } = await api.get("/admin/users", { params: {
      search: userSearch, provider: userProvider === "all" ? undefined : userProvider,
      country: userCountry || undefined, date_from: userDateFrom || undefined, date_to: userDateTo || undefined,
      skip: userSkip, limit: 50,
    } });
    setUsers(data.users);
    setUsersTotal(data.total);
  }, [userSearch, userSkip, userProvider, userCountry, userDateFrom, userDateTo]);

  const loadCountries = useCallback(async () => {
    try { const { data } = await api.get("/admin/users/countries"); setCountries(data.countries); } catch { /* ignore */ }
  }, []);

  const exportUsers = async (format) => {
    setExporting(true);
    try {
      const { data } = await api.get("/admin/users/export", {
        params: {
          format, search: userSearch, provider: userProvider === "all" ? undefined : userProvider,
          country: userCountry || undefined, date_from: userDateFrom || undefined, date_to: userDateTo || undefined,
        },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `usuarios.${format === "pdf" ? "pdf" : "xlsx"}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Exportado a ${format === "pdf" ? "PDF" : "Excel"}`);
    } catch {
      toast.error("No se pudo exportar");
    } finally {
      setExporting(false);
    }
  };

  const loadHero = useCallback(async () => {
    try { const { data } = await api.get("/admin/hero"); setHeroId(data.movie_id); } catch { /* ignore */ }
  }, []);

  const loadMovies = useCallback(async () => {
    const { data } = await api.get("/admin/movies", { params: { search: movieSearch, skip: movieSkip, limit: 50 } });
    setMovies(data.movies);
    setMoviesTotal(data.total);
  }, [movieSearch, movieSkip]);

  const setAsHero = async (id) => {
    try {
      await api.put("/admin/hero", { movie_id: id });
      setHeroId(id);
      toast.success("Portada del inicio actualizada");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const searchTmdb = async (e, page = 1) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!tmdbQuery.trim()) return;
    setTmdbSearching(true);
    try {
      const { data } = await api.get("/admin/tmdb/search", { params: { query: tmdbQuery, search_type: tmdbType, page } });
      setTmdbResults(data.results);
      setTmdbMeta({ page: data.page, total_pages: data.total_pages, total_results: data.total_results, person_name: data.person_name });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setTmdbSearching(false);
    }
  };

  const addTmdb = async (r) => {
    const key = `${r.media_type}-${r.tmdb_id}`;
    setAddingKey(key);
    try {
      const { data } = await api.post("/admin/tmdb/add", { tmdb_id: r.tmdb_id, media_type: r.media_type });
      toast.success(data.added ? `"${r.title}" agregada al catálogo` : `"${r.title}" ya estaba en el catálogo`);
      setTmdbResults((prev) => prev.map((x) => (x.tmdb_id === r.tmdb_id && x.media_type === r.media_type ? { ...x, already_added: true } : x)));
      loadMovies();
      loadAll();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setAddingKey(null);
    }
  };

  const loadComments = useCallback(async () => {
    const { data } = await api.get("/admin/comments", { params: { skip: commentsSkip, limit: 50 } });
    setCommentsList(data.comments);
    setCommentsTotal(data.total);
  }, [commentsSkip]);

  const delComment = async (id) => {
    if (!window.confirm("¿Eliminar este comentario?")) return;
    await api.delete(`/admin/comments/${id}`);
    toast.success("Comentario eliminado");
    loadComments();
  };

  const loadAll = useCallback(async () => {
    const [s, c] = await Promise.all([
      api.get("/admin/stats"), api.get("/categories"),
    ]);
    setStats(s.data); setCategories(c.data);
    loadUsers();
    loadHero();
    loadCountries();
  }, [loadUsers, loadHero, loadCountries]);

  useEffect(() => { loadAll(); }, [loadAll]);

  useEffect(() => {
    const t = setTimeout(() => loadUsers(), 300);
    return () => clearTimeout(t);
  }, [userSearch, userSkip, userProvider, userCountry, userDateFrom, userDateTo, loadUsers]);

  useEffect(() => {
    const t = setTimeout(() => loadMovies(), 300);
    return () => clearTimeout(t);
  }, [movieSearch, movieSkip, loadMovies]);

  useEffect(() => { if (tab === "comments") loadComments(); }, [tab, loadComments]);
  useEffect(() => { if (tab === "revista") loadAdminArticles(); }, [tab, loadAdminArticles]);

  const importSubscribers = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImportingSubs(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/import/subscribers", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Importados ${data.imported} suscriptores (${data.skipped} omitidos)`);
      loadAll();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setImportingSubs(false);
      e.target.value = "";
    }
  };

  const loadImpStatus = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/import/tmdb/status");
      setImp(data);
      return data;
    } catch { return null; }
  }, []);

  useEffect(() => { if (tab === "import") loadImpStatus(); }, [tab, loadImpStatus]);
  useEffect(() => { if (tab === "ads") loadAds(); }, [tab, loadAds]);

  useEffect(() => {
    if (!imp?.running) return;
    const t = setInterval(async () => {
      const d = await loadImpStatus();
      if (d && !d.running) { clearInterval(t); loadAll(); }
    }, 2500);
    return () => clearInterval(t);
  }, [imp?.running, loadImpStatus, loadAll]);

  const startImport = async () => {
    try {
      await api.post("/admin/import/tmdb", impForm);
      toast.success("Importación iniciada. Esto puede tardar unos minutos.");
      setImp({ running: true, imported: 0 });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const [autoDetectingPlatforms, setAutoDetectingPlatforms] = useState(false);

  const handleAutoDetectPlatforms = async () => {
    if (!editing) return;
    let tmdbId = null;
    let mediaType = editing.media_type || "movie";

    if (editing.id && editing.id.startsWith("tmdb-")) {
      const parts = editing.id.split("-");
      if (parts.length >= 3) {
        mediaType = parts[1] === "tv" ? "tv" : "movie";
        tmdbId = parts[2];
      }
    }

    setAutoDetectingPlatforms(true);
    try {
      if (!tmdbId && editing.title) {
        const { data: sData } = await api.get("/admin/tmdb/search", { params: { query: editing.title, search_type: "title", page: 1 } });
        if (sData.results && sData.results.length > 0) {
          tmdbId = sData.results[0].tmdb_id;
          mediaType = sData.results[0].media_type || mediaType;
        }
      }

      if (!tmdbId) {
        toast.error("No se encontró el ID de TMDb para esta película");
        return;
      }

      const { data } = await api.get(`/admin/tmdb/${tmdbId}/watch-providers`, { params: { media_type: mediaType } });
      if (data.platforms && data.platforms.length > 0) {
        const existing = Array.isArray(editing.platforms) ? [...editing.platforms] : [];
        const existingNames = new Set(existing.map((p) => p.name?.toLowerCase().trim()));
        let addedCount = 0;

        for (const p of data.platforms) {
          if (!existingNames.has(p.name?.toLowerCase().trim())) {
            existing.push({
              name: p.name,
              url: p.url || "",
              type: p.type || "stream",
              logo_url: p.logo_url || null,
            });
            addedCount++;
          }
        }

        setEditing({ ...editing, platforms: existing });
        toast.success(`Se detectaron y agregaron ${addedCount} plataformas oficiales desde TMDb / JustWatch`);
      } else {
        toast.info("No se encontraron plataformas de streaming activas para este título en TMDb");
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Error al consultar plataformas en TMDb");
    } finally {
      setAutoDetectingPlatforms(false);
    }
  };

  const saveMovie = async (e) => {
    e.preventDefault();
    const payload = {
      ...editing,
      year: editing.year ? parseInt(editing.year) : null,
      genres: Array.isArray(editing.genres) ? editing.genres : String(editing.genres).split(",").map((x) => x.trim()).filter(Boolean),
      cast: Array.isArray(editing.cast) ? editing.cast : String(editing.cast).split(",").map((x) => x.trim()).filter(Boolean),
      platforms: Array.isArray(editing.platforms) ? editing.platforms.filter((p) => p && p.name && p.name.trim()) : [],
    };
    try {
      if (editing.id) await api.put(`/movies/${editing.id}`, payload);
      else await api.post("/movies", payload);
      toast.success("Película guardada");
      setEditing(null);
      loadMovies();
      loadAll();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const delMovie = async (id) => {
    if (!window.confirm("¿Eliminar esta película?")) return;
    await api.delete(`/movies/${id}`);
    toast.success("Película eliminada");
    loadMovies();
    loadAll();
  };

  const importMoviesFromFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      setImportingMovies(true);
      const formData = new FormData();
      formData.append("file", file);
      const { data } = await api.post("/admin/import/movies", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Importación completada: ${data.imported} películas agregadas (${data.skipped} omitidas o duplicadas)`);
      loadMovies();
      loadAll();
      setShowImportModal(false);
    } catch (err) {
      toast.error("Error al importar películas: " + formatApiError(err.response?.data?.detail || err.message));
    } finally {
      setImportingMovies(false);
      e.target.value = "";
    }
  };

  const importMoviesFromUrl = async () => {
    if (!importUrl.trim()) {
      toast.error("Ingresa una URL de sitio web o API válida");
      return;
    }
    try {
      setImportingMovies(true);
      const { data } = await api.post("/admin/import/movies-url", { url: importUrl });
      toast.success(`Importación desde Web completada: ${data.imported} películas agregadas (${data.skipped} omitidas)`);
      loadMovies();
      loadAll();
      setImportUrl("");
      setShowImportModal(false);
    } catch (err) {
      toast.error("Error al importar desde URL: " + formatApiError(err.response?.data?.detail || err.message));
    } finally {
      setImportingMovies(false);
    }
  };

  const addCategory = async (e) => {
    e.preventDefault();
    if (!catName.trim()) return;
    await api.post("/categories", { name: catName });
    setCatName("");
    toast.success("Categoría creada");
    loadAll();
  };

  const delCategory = async (id) => {
    if (!window.confirm("¿Eliminar categoría?")) return;
    await api.delete(`/categories/${id}`);
    loadAll();
  };

  const [userModal, setUserModal] = useState({ isOpen: false, user: null, result: null, type: null });

  const setRole = async (uid, role) => {
    await api.put(`/admin/users/${uid}/role`, { role });
    toast.success("Rol actualizado");
    loadAll();
  };

  const resetUserPassword = async (targetUser) => {
    try {
      const { data } = await api.post(`/admin/users/${targetUser.user_id}/reset-password`);
      setUserModal({ isOpen: true, user: targetUser, result: data, type: "reset_link" });
      toast.success(data.message || "Enlace de restablecimiento generado");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Error al restablecer contraseña");
    }
  };

  const generateTempPassword = async (targetUser) => {
    try {
      const { data } = await api.post(`/admin/users/${targetUser.user_id}/temp-password`);
      setUserModal({ isOpen: true, user: targetUser, result: data, type: "temp_password" });
      navigator.clipboard.writeText(data.temp_password);
      toast.success(`Contraseña provisional: ${data.temp_password} (Copiada al portapapeles)`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Error al generar contraseña provisional");
    }
  };

  const toggleUserStatus = async (targetUser) => {
    const isSuspended = targetUser.status === "suspended";
    const nextStatus = isSuspended ? "active" : "suspended";
    const reason = !isSuspended ? prompt("Motivo de suspensión (opcional):", "Incumplimiento de términos o actividad sospechosa") : "";
    
    try {
      const { data } = await api.post(`/admin/users/${targetUser.user_id}/status`, {
        status: nextStatus,
        reason: reason || undefined
      });
      toast.success(data.message);
      loadAll();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Error al cambiar estado");
    }
  };

  const revokeUserSessions = async (targetUser) => {
    if (!window.confirm(`¿Cerrar todas las sesiones activas de ${targetUser.name || targetUser.email}?`)) return;
    try {
      const { data } = await api.post(`/admin/users/${targetUser.user_id}/revoke-sessions`);
      toast.success(data.message || "Sesiones cerradas con éxito");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Error al cerrar sesiones");
    }
  };

  const delUser = async (uid) => {
    if (!window.confirm("¿Eliminar usuario?")) return;
    try {
      await api.delete(`/admin/users/${uid}`);
      toast.success("Usuario eliminado");
      loadAll();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const toggleCat = (id) => {
    const has = editing.category_ids.includes(id);
    setEditing({ ...editing, category_ids: has ? editing.category_ids.filter((x) => x !== id) : [...editing.category_ids, id] });
  };

  const [optimizingMedia, setOptimizingMedia] = useState(false);

  const optimizeAllCatalogMedia = async () => {
    if (!window.confirm("¿Deseas descargar y optimizar en WebP todas las imágenes del catálogo (películas, festivales, artículos) para guardarlas en nuestros servidores?")) return;
    setOptimizingMedia(true);
    try {
      const { data } = await api.post("/admin/media/optimize-all");
      toast.success(data.message || "Optimización de catálogo iniciada");
      setTimeout(() => loadAll(), 3000);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Error al optimizar imágenes");
    } finally {
      setOptimizingMedia(false);
    }
  };

  const tabs = [
    { key: "live", label: "Live Analytics & Push", icon: Radio, isLive: true },
    { key: "movies", label: "Películas", icon: Film },
    { key: "search", label: "Buscar TMDb", icon: Search },
    { key: "festivals", label: "Festivales LGBTQ+", icon: Globe },
    { key: "revista", label: "Revista / Artículos", icon: BookOpen },
    { key: "newsletter", label: "Email Marketing / Correo", icon: Mail },
    { key: "dmca", label: "DMCA / Reclamaciones", icon: Scale },
    { key: "categories", label: "Categorías", icon: Tags },
    { key: "users", label: "Usuarios", icon: Users },
    { key: "comments", label: "Comentarios", icon: MessageSquare },
    { key: "import", label: "Importar TMDb", icon: Download },
    { key: "ads", label: "Publicidad", icon: Megaphone },
  ];

  return (
    <div className="min-h-screen bg-[#050505] flex flex-col md:flex-row" data-testid="admin-dashboard">
      <aside className="md:w-64 border-b md:border-b-0 md:border-r border-white/10 bg-[#0A0A0C] p-6 flex md:flex-col gap-2">
        <Link to="/" className="hidden md:flex items-center gap-2 text-zinc-400 hover:text-white mb-6 text-sm" data-testid="admin-back-home">
          <ArrowLeft size={16} /> Volver al sitio
        </Link>
        <h2 className="hidden md:block font-heading text-lg font-bold mb-4">Panel Admin</h2>
        {tabs.map((t) => (
          <button
            key={t.key}
            data-testid={`admin-tab-${t.key}`}
            onClick={() => setTab(t.key)}
            className={`flex items-center justify-between px-4 py-2.5 text-sm transition-colors rounded-lg ${tab === t.key ? "bg-[#E11D48]/15 text-[#E11D48] border-l-2 border-[#E11D48]" : "text-zinc-400 hover:text-white"}`}
          >
            <div className="flex items-center gap-3">
              <t.icon size={16} className={t.isLive ? "text-emerald-400" : ""} />
              <span>{t.label}</span>
            </div>
            {t.isLive && (
              <span className="flex h-2 w-2 relative">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
              </span>
            )}
          </button>
        ))}
        <Link to="/" className="md:hidden ml-auto text-zinc-400" data-testid="admin-back-home-m"><ArrowLeft size={18} /></Link>
      </aside>

      <main className="flex-1 p-6 md:p-10">
        {stats && tab !== "live" && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-10">
            {[
              { label: "Películas", val: stats.movies, icon: Film },
              { label: "Artículos", val: stats.articles || adminArticles.length, icon: BookOpen },
              { label: "Categorías", val: stats.categories, icon: Tags },
              { label: "Usuarios", val: stats.users, icon: Users },
              { label: "Calificaciones", val: stats.ratings, icon: BarChart3 },
            ].map((s) => (
              <div key={s.label} className="border border-white/10 p-5 hover:-translate-y-1 transition-transform" data-testid={`stat-${s.label}`}>
                <s.icon size={18} className="text-[#E11D48] mb-3" />
                <p className="font-heading text-3xl font-light">{s.val}</p>
                <p className="text-xs tracking-wider uppercase text-zinc-500 mt-1">{s.label}</p>
              </div>
            ))}
          </div>
        )}

        {tab === "live" && <LiveAdminTab />}

        {tab === "movies" && (
          <div>
            <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
              <div>
                <h1 className="font-heading text-2xl">Películas</h1>
                <p className="text-xs text-zinc-500 mt-1 flex items-center gap-1"><ImageIcon size={12} /> Pulsa el icono de imagen para elegir la portada del inicio.</p>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <button
                  onClick={optimizeAllCatalogMedia}
                  disabled={optimizingMedia}
                  className="bg-zinc-900 border border-emerald-500/30 text-emerald-400 px-4 py-2.5 flex items-center gap-2 hover:bg-emerald-950/40 transition-colors text-sm font-medium rounded-sm disabled:opacity-50"
                  title="Descarga todas las imágenes externas y las convierte a formato WebP optimizado en nuestros servidores"
                >
                  {optimizingMedia ? <Loader2 size={16} className="animate-spin text-emerald-400" /> : <HardDrive size={16} className="text-emerald-400" />}
                  <span>Optimizar Catálogo a WebP</span>
                </button>
                <button
                  data-testid="open-import-movies-modal"
                  onClick={() => setShowImportModal(true)}
                  className="bg-zinc-800 border border-white/20 text-white px-4 py-2.5 flex items-center gap-2 hover:bg-zinc-700 transition-colors text-sm font-medium"
                >
                  <Upload size={16} className="text-[#E11D48]" /> Importar Catálogo (Excel / Web)
                </button>
                <button data-testid="admin-add-movie-button" onClick={() => setEditing({ ...EMPTY_MOVIE })} className="bg-[#E11D48] text-white px-5 py-2.5 flex items-center gap-2 hover:bg-[#BE123C] transition-colors text-sm font-medium">
                  <Plus size={16} /> Agregar
                </button>
              </div>
            </div>
            <div className="flex items-center gap-2 border-b border-white/20 focus-within:border-[#E11D48] transition-colors max-w-md mb-4">
              <Search size={16} className="text-zinc-400" />
              <input
                data-testid="movie-search-input"
                value={movieSearch}
                onChange={(e) => { setMovieSkip(0); setMovieSearch(e.target.value); }}
                placeholder="Buscar película por título..."
                className="bg-transparent py-2 text-sm outline-none flex-1 placeholder:text-zinc-500"
              />
            </div>
            <p className="text-xs text-zinc-500 mb-4">{moviesTotal} títulos en total</p>
            <div className="border border-white/10 divide-y divide-white/10">
              {movies.map((m) => (
                <div key={m.id} className="flex items-center gap-4 p-4 hover:bg-white/[0.02]" data-testid={`admin-movie-${m.id}`}>
                  {m.poster_url ? (
                    <img
                      src={m.poster_url}
                      alt=""
                      className="w-10 h-14 object-cover bg-zinc-800"
                      onError={(e) => {
                        e.currentTarget.onerror = null;
                        e.currentTarget.src = "https://images.unsplash.com/photo-1485846234645-a62644f84728?w=500&auto=format&fit=crop&q=80";
                      }}
                    />
                  ) : <div className="w-10 h-14 bg-zinc-800" />}
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate flex items-center gap-1.5 flex-wrap">
                      <span>{m.title}</span>
                      {m.upcoming && (
                        <span className="text-[10px] bg-gradient-to-r from-amber-500 to-rose-500 text-white font-bold px-2 py-0.5 rounded-full uppercase tracking-wider shadow-xs">
                          {m.custom_badge || "Próximo estreno"}
                        </span>
                      )}
                      {m.featured && <span className="text-[#E11D48] text-xs font-semibold">★ destacada</span>}
                      {heroId === m.id && <span className="text-emerald-400 text-xs font-semibold">● portada</span>}
                    </p>
                    <p className="text-xs text-zinc-500">
                      {m.upcoming && m.release_date ? `Estreno: ${m.release_date} · ` : ""}{m.year} · {m.genres?.join(", ")}
                    </p>
                  </div>
                  <button
                    data-testid={`set-hero-${m.id}`}
                    onClick={() => setAsHero(m.id)}
                    title="Usar como portada del inicio"
                    className={`p-2 transition-colors ${heroId === m.id ? "text-emerald-400" : "text-zinc-400 hover:text-white"}`}
                  >
                    <ImageIcon size={16} />
                  </button>
                  <button data-testid={`edit-movie-${m.id}`} onClick={() => setEditing({ ...m, year: m.year || "" })} className="text-zinc-400 hover:text-white p-2"><Pencil size={16} /></button>
                  <button data-testid={`delete-movie-${m.id}`} onClick={() => delMovie(m.id)} className="text-zinc-400 hover:text-[#E11D48] p-2"><Trash2 size={16} /></button>
                </div>
              ))}
              {movies.length === 0 && <div className="p-6 text-zinc-500 text-sm">No se encontraron películas.</div>}
            </div>
            {moviesTotal > 50 && (
              <div className="flex items-center justify-between mt-4">
                <button data-testid="movies-prev" disabled={movieSkip === 0} onClick={() => setMovieSkip(Math.max(0, movieSkip - 50))} className="text-sm text-zinc-400 hover:text-white disabled:opacity-30">← Anteriores</button>
                <span className="text-xs text-zinc-500">{movieSkip + 1}–{Math.min(movieSkip + 50, moviesTotal)} de {moviesTotal}</span>
                <button data-testid="movies-next" disabled={movieSkip + 50 >= moviesTotal} onClick={() => setMovieSkip(movieSkip + 50)} className="text-sm text-zinc-400 hover:text-white disabled:opacity-30">Siguientes →</button>
              </div>
            )}
          </div>
        )}

        {tab === "search" && (
          <div className="max-w-3xl">
            <div className="flex items-center gap-3 mb-2">
              <Search size={22} className="text-[#E11D48]" />
              <h1 className="font-heading text-2xl">Buscar y agregar desde TMDb</h1>
            </div>
            <p className="text-zinc-400 text-sm mb-6">Busca por título, por director/actor o por número (ID de TMDb) y agrega títulos al catálogo.</p>

            <div className="flex flex-wrap gap-2 mb-4">
              {[["title", "Por título"], ["person", "Por director / actor"], ["id", "Por número (ID)"]].map(([v, l]) => (
                <button
                  key={v}
                  data-testid={`tmdb-type-${v}`}
                  type="button"
                  onClick={() => { setTmdbType(v); setTmdbResults(null); }}
                  className={`px-4 py-2 text-sm border transition-colors ${tmdbType === v ? "border-[#E11D48] text-[#E11D48] bg-[#E11D48]/10" : "border-white/15 text-zinc-400 hover:text-white"}`}
                >
                  {l}
                </button>
              ))}
            </div>

            <form onSubmit={searchTmdb} className="flex gap-3 mb-6">
              <input
                data-testid="tmdb-search-input"
                value={tmdbQuery}
                onChange={(e) => setTmdbQuery(e.target.value)}
                placeholder={tmdbType === "person" ? "Ej: Pedro Almodóvar" : tmdbType === "id" ? "Ej: 550" : "Ej: Call Me by Your Name"}
                className="flex-1 bg-transparent border-b border-white/20 py-2 outline-none focus:border-[#E11D48]"
              />
              <button data-testid="tmdb-search-button" type="submit" disabled={tmdbSearching} className="bg-[#E11D48] text-white px-6 py-2 hover:bg-[#BE123C] transition-colors flex items-center gap-2 disabled:opacity-50">
                {tmdbSearching ? <Loader2 size={16} className="animate-spin" /> : <Search size={16} />} Buscar
              </button>
            </form>

            {tmdbResults && tmdbMeta.person_name && (
              <p className="text-sm text-zinc-400 mb-3" data-testid="tmdb-person-name">Filmografía de <span className="text-white">{tmdbMeta.person_name}</span> · {tmdbMeta.total_results} títulos</p>
            )}
            {tmdbResults && tmdbResults.length === 0 && <p className="text-zinc-500 text-sm">Sin resultados.</p>}
            {tmdbResults && tmdbResults.length > 0 && (
              <div className="border border-white/10 divide-y divide-white/10" data-testid="tmdb-results">
                {tmdbResults.map((r) => {
                  const key = `${r.media_type}-${r.tmdb_id}`;
                  return (
                    <div key={key} className="flex items-center gap-4 p-4" data-testid={`tmdb-result-${r.tmdb_id}`}>
                      {r.poster_url ? (
                        <img
                          src={r.poster_url}
                          alt=""
                          className="w-10 h-14 object-cover bg-zinc-800"
                          onError={(e) => {
                            e.currentTarget.onerror = null;
                            e.currentTarget.src = "https://images.unsplash.com/photo-1485846234645-a62644f84728?w=500&auto=format&fit=crop&q=80";
                          }}
                        />
                      ) : <div className="w-10 h-14 bg-zinc-800" />}
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{r.title} <span className="text-xs text-zinc-500">({r.media_type === "tv" ? "Serie" : "Película"}{r.year ? ` · ${r.year}` : ""})</span>{r.role ? <span className="text-xs text-[#E11D48] ml-2">{r.role}</span> : null}</p>
                        <p className="text-xs text-zinc-500 line-clamp-2">{r.overview || "Sin sinopsis."}</p>
                      </div>
                      {r.already_added ? (
                        <span className="text-emerald-400 text-sm flex items-center gap-1 shrink-0"><Check size={15} /> En catálogo</span>
                      ) : (
                        <button
                          data-testid={`tmdb-add-${r.tmdb_id}`}
                          onClick={() => addTmdb(r)}
                          disabled={addingKey === key}
                          className="bg-[#E11D48] text-white px-4 py-2 text-sm hover:bg-[#BE123C] transition-colors flex items-center gap-2 shrink-0 disabled:opacity-50"
                        >
                          {addingKey === key ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />} Agregar
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            {tmdbResults && tmdbMeta.total_pages > 1 && (
              <div className="flex items-center justify-between mt-4" data-testid="tmdb-pagination">
                <button data-testid="tmdb-prev" disabled={tmdbMeta.page <= 1 || tmdbSearching} onClick={() => searchTmdb(null, tmdbMeta.page - 1)} className="text-sm text-zinc-400 hover:text-white disabled:opacity-30">← Anteriores</button>
                <span className="text-xs text-zinc-500">Página {tmdbMeta.page} de {tmdbMeta.total_pages}</span>
                <button data-testid="tmdb-next" disabled={tmdbMeta.page >= tmdbMeta.total_pages || tmdbSearching} onClick={() => searchTmdb(null, tmdbMeta.page + 1)} className="text-sm text-zinc-400 hover:text-white disabled:opacity-30">Siguientes →</button>
              </div>
            )}
          </div>
        )}

        {tab === "categories" && (
          <div>
            <h1 className="font-heading text-2xl mb-6">Categorías</h1>
            <form onSubmit={addCategory} className="flex gap-3 mb-6 max-w-md">
              <input data-testid="category-name-input" value={catName} onChange={(e) => setCatName(e.target.value)} placeholder="Nueva categoría" className="flex-1 bg-transparent border-b border-white/20 py-2 outline-none focus:border-[#E11D48]" />
              <button data-testid="add-category-button" className="bg-[#E11D48] text-white px-5 py-2 hover:bg-[#BE123C] transition-colors"><Plus size={16} /></button>
            </form>
            <div className="flex flex-wrap gap-3">
              {categories.map((c) => (
                <div key={c.id} className="flex items-center gap-3 border border-white/10 px-4 py-2.5" data-testid={`admin-category-${c.id}`}>
                  <span>{c.name}</span>
                  <button data-testid={`delete-category-${c.id}`} onClick={() => delCategory(c.id)} className="text-zinc-500 hover:text-[#E11D48]"><X size={15} /></button>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "users" && (
          <div>
            <div className="flex flex-wrap items-center justify-between gap-4 mb-2">
              <h1 className="font-heading text-2xl">Usuarios</h1>
              <div className="flex flex-wrap items-center gap-2">
                <button data-testid="export-xlsx-button" onClick={() => exportUsers("xlsx")} disabled={exporting} className="bg-emerald-600 text-white px-4 py-2.5 flex items-center gap-2 hover:bg-emerald-700 transition-colors text-sm disabled:opacity-50">
                  <Download size={16} /> Excel
                </button>
                <button data-testid="export-pdf-button" onClick={() => exportUsers("pdf")} disabled={exporting} className="bg-rose-700 text-white px-4 py-2.5 flex items-center gap-2 hover:bg-rose-800 transition-colors text-sm disabled:opacity-50">
                  <Download size={16} /> PDF
                </button>
                <label data-testid="import-subscribers-button" className="cursor-pointer bg-[#E11D48] text-white px-4 py-2.5 flex items-center gap-2 hover:bg-[#BE123C] transition-colors text-sm font-medium">
                  {importingSubs ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
                  {importingSubs ? "Importando..." : "Importar Usuarios (Excel / CSV)"}
                  <input type="file" accept=".xlsx,.xls,.csv,.json,.txt" className="hidden" onChange={importSubscribers} disabled={importingSubs} data-testid="subscribers-file-input" />
                </label>
              </div>
            </div>
            <p className="flex items-center gap-2 text-xs text-zinc-500 mb-3"><Lock size={12} /> Datos privados. Solo visibles para administradores.</p>

            <div className="p-3.5 mb-6 bg-zinc-900/80 border border-white/10 rounded-lg text-xs text-zinc-300 leading-relaxed flex items-start gap-3">
              <Upload size={18} className="text-[#E11D48] shrink-0 mt-0.5" />
              <div>
                <strong className="text-white block mb-0.5">¿Tienes la base de datos previa en Excel?</strong>
                Sube directamente tu archivo <code className="text-rose-400 bg-white/5 px-1 py-0.5 rounded">.xlsx</code> o <code className="text-rose-400 bg-white/5 px-1 py-0.5 rounded">.xls</code> con la lista de correos/usuarios registrados. El sistema importará todos los registros sin duplicados para que puedan seguir accediendo a La Caja LGBT.
              </div>
            </div>

            <div className="flex flex-wrap gap-2 mb-4">
              {[["all", "Todos"], ["registered", "Nuevos registros"], ["legacy", "Legacy (WordPress)"]].map(([v, l]) => (
                <button
                  key={v}
                  data-testid={`user-filter-${v}`}
                  onClick={() => { setUserSkip(0); setUserProvider(v); }}
                  className={`px-4 py-2 text-sm border transition-colors ${userProvider === v ? "border-[#E11D48] text-[#E11D48] bg-[#E11D48]/10" : "border-white/15 text-zinc-400 hover:text-white"}`}
                >
                  {l}
                </button>
              ))}
            </div>

            <div className="flex flex-wrap items-end gap-4 mb-4">
              <div>
                <label className="text-[10px] tracking-wider uppercase text-zinc-500 block mb-1">País</label>
                <select
                  data-testid="user-country-filter"
                  value={userCountry}
                  onChange={(e) => { setUserSkip(0); setUserCountry(e.target.value); }}
                  className="bg-[#0A0A0C] border border-white/15 px-3 py-2 text-sm outline-none focus:border-[#E11D48]"
                >
                  <option value="">Todos los países</option>
                  {countries.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[10px] tracking-wider uppercase text-zinc-500 block mb-1">Desde</label>
                <input data-testid="user-date-from" type="date" value={userDateFrom} onChange={(e) => { setUserSkip(0); setUserDateFrom(e.target.value); }} className="bg-[#0A0A0C] border border-white/15 px-3 py-2 text-sm outline-none focus:border-[#E11D48] text-zinc-300" />
              </div>
              <div>
                <label className="text-[10px] tracking-wider uppercase text-zinc-500 block mb-1">Hasta</label>
                <input data-testid="user-date-to" type="date" value={userDateTo} onChange={(e) => { setUserSkip(0); setUserDateTo(e.target.value); }} className="bg-[#0A0A0C] border border-white/15 px-3 py-2 text-sm outline-none focus:border-[#E11D48] text-zinc-300" />
              </div>
              {(userCountry || userDateFrom || userDateTo) && (
                <button data-testid="user-clear-filters" onClick={() => { setUserSkip(0); setUserCountry(""); setUserDateFrom(""); setUserDateTo(""); }} className="text-sm text-zinc-400 hover:text-white flex items-center gap-1 pb-2">
                  <X size={14} /> Limpiar
                </button>
              )}
            </div>

            <div className="flex items-center gap-2 border-b border-white/20 focus-within:border-[#E11D48] transition-colors max-w-md mb-4">
              <Search size={16} className="text-zinc-400" />
              <input
                data-testid="user-search-input"
                value={userSearch}
                onChange={(e) => { setUserSkip(0); setUserSearch(e.target.value); }}
                placeholder="Buscar por nombre o correo..."
                className="bg-transparent py-2 text-sm outline-none flex-1 placeholder:text-zinc-500"
              />
            </div>
            <p className="text-xs text-zinc-500 mb-4">{usersTotal} usuarios en total</p>

            <div className="border border-white/10 divide-y divide-white/10">
              {users.map((u) => {
                const isSuspended = u.status === "suspended";
                return (
                  <div key={u.user_id} className={`flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4 p-4 transition-colors ${isSuspended ? "bg-rose-950/15" : "hover:bg-white/[0.02]"}`} data-testid={`admin-user-${u.user_id}`}>
                    <div className="flex items-start sm:items-center gap-3.5 min-w-0 flex-1">
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-sm uppercase font-bold shrink-0 border ${isSuspended ? "bg-rose-500/10 text-rose-400 border-rose-500/30" : "bg-[#E11D48]/15 text-[#E11D48] border-[#E11D48]/30"}`}>
                        {(u.name || u.email)[0]}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="font-semibold text-white truncate text-sm">{u.name || "Sin nombre"}</p>
                          {u.provider === "legacy" && (
                            <span className="text-[10px] uppercase tracking-wider text-zinc-400 border border-white/15 px-1.5 py-0.5 rounded">Legacy</span>
                          )}
                          <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium border ${isSuspended ? "bg-rose-500/20 text-rose-300 border-rose-500/30" : "bg-emerald-500/15 text-emerald-400 border-emerald-500/25"}`}>
                            {isSuspended ? "Suspendido" : "Activo"}
                          </span>
                          {u.require_password_change && (
                            <span className="text-[10px] px-2 py-0.5 rounded-full font-medium bg-amber-500/15 text-amber-300 border border-amber-500/25">
                              Cambio de clave pendiente
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-zinc-400 truncate mt-0.5">{u.email} · {u.provider}</p>
                        {u.provider !== "legacy" && (
                          <p className="text-[11px] text-zinc-500 truncate mt-0.5" data-testid={`user-meta-${u.user_id}`}>
                            {[
                              u.created_at ? `Registro: ${new Date(u.created_at).toLocaleDateString()}` : null,
                              u.phone ? `Tel: ${u.phone}` : null,
                              u.signup_country ? `País: ${u.signup_country}` : null,
                              u.signup_ip ? `IP: ${u.signup_ip}` : null,
                            ].filter(Boolean).join(" · ") || "Sin datos de registro"}
                          </p>
                        )}
                        {u.legacy && (u.legacy.phone || u.legacy.city || u.legacy.country || u.legacy.status) && (
                          <p className="text-[11px] text-zinc-500 truncate mt-0.5">
                            {[u.legacy.status, u.legacy.phone, u.legacy.city, u.legacy.country].filter(Boolean).join(" · ")}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-2 self-end lg:self-center shrink-0">
                      {/* ACCIONES DE CONTRASEÑA Y SEGURIDAD */}
                      <button
                        type="button"
                        onClick={() => resetUserPassword(u)}
                        title="Enviar enlace o generar token de restablecimiento de contraseña"
                        className="px-2.5 py-1.5 bg-zinc-900 hover:bg-zinc-800 border border-white/15 text-zinc-200 hover:text-white rounded-lg text-xs font-medium transition-colors flex items-center gap-1.5"
                      >
                        <KeyRound size={13} className="text-[#E11D48]" />
                        <span>Restablecer</span>
                      </button>

                      <button
                        type="button"
                        onClick={() => generateTempPassword(u)}
                        title="Generar y copiar una contraseña provisional inmediata"
                        className="px-2.5 py-1.5 bg-zinc-900 hover:bg-zinc-800 border border-white/15 text-zinc-200 hover:text-white rounded-lg text-xs font-medium transition-colors flex items-center gap-1.5"
                      >
                        <Key size={13} className="text-amber-400" />
                        <span>Clave Temporal</span>
                      </button>

                      <button
                        type="button"
                        onClick={() => toggleUserStatus(u)}
                        title={isSuspended ? "Reactivar cuenta de usuario" : "Suspender cuenta de usuario"}
                        className={`px-2.5 py-1.5 border rounded-lg text-xs font-medium transition-colors flex items-center gap-1.5 ${isSuspended ? "bg-emerald-950/40 hover:bg-emerald-900/60 text-emerald-300 border-emerald-500/30" : "bg-zinc-900 hover:bg-rose-950/40 text-zinc-300 hover:text-rose-300 border-white/15 hover:border-rose-500/30"}`}
                      >
                        {isSuspended ? <ShieldCheck size={13} className="text-emerald-400" /> : <Ban size={13} className="text-rose-400" />}
                        <span>{isSuspended ? "Reactivar" : "Suspender"}</span>
                      </button>

                      <button
                        type="button"
                        onClick={() => revokeUserSessions(u)}
                        title="Cerrar todas las sesiones activas del usuario"
                        className="p-1.5 bg-zinc-900 hover:bg-zinc-800 border border-white/15 text-zinc-400 hover:text-amber-400 rounded-lg transition-colors"
                      >
                        <LogOut size={14} />
                      </button>

                      <select
                        data-testid={`user-role-${u.user_id}`}
                        value={u.role}
                        onChange={(e) => setRole(u.user_id, e.target.value)}
                        disabled={u.user_id === user.user_id}
                        className="bg-[#0A0A0C] border border-white/15 rounded-lg px-2.5 py-1.5 text-xs outline-none focus:border-[#E11D48] disabled:opacity-50 text-white"
                      >
                        <option value="user">Usuario</option>
                        <option value="editor">Editor</option>
                        <option value="admin">Admin</option>
                      </select>

                      <button
                        data-testid={`delete-user-${u.user_id}`}
                        onClick={() => delUser(u.user_id)}
                        disabled={u.user_id === user.user_id}
                        className="text-zinc-400 hover:text-[#E11D48] p-1.5 rounded-lg border border-transparent hover:border-rose-500/20 disabled:opacity-30 transition-colors"
                        title="Eliminar usuario permanentemente"
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </div>
                );
              })}
              {users.length === 0 && <div className="p-6 text-zinc-500 text-sm text-center">No se encontraron usuarios.</div>}
            </div>

            {/* MODAL RESULTADO DE SEGURIDAD / RESTABLECIMIENTO */}
            {userModal.isOpen && (
              <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
                <div className="bg-[#121214] border border-white/15 rounded-2xl max-w-lg w-full p-6 shadow-2xl relative overflow-hidden">
                  <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#E11D48] via-purple-600 to-[#E11D48]" />
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-base font-bold text-white font-heading flex items-center gap-2">
                      <KeyRound className="w-5 h-5 text-[#E11D48]" />
                      <span>{userModal.type === "reset_link" ? "Enlace de Restablecimiento Generado" : "Contraseña Temporal Asignada"}</span>
                    </h3>
                    <button
                      onClick={() => setUserModal({ isOpen: false, user: null, result: null, type: null })}
                      className="text-zinc-400 hover:text-white text-sm"
                    >
                      ✕
                    </button>
                  </div>

                  <div className="space-y-4">
                    <p className="text-xs text-zinc-300">
                      Usuario: <strong className="text-white">{userModal.user?.name || "—"}</strong> ({userModal.user?.email})
                    </p>

                    {userModal.type === "reset_link" && (
                      <div className="space-y-3">
                        <div className="p-3.5 bg-zinc-900 border border-white/10 rounded-xl space-y-2">
                          <label className="text-[10px] uppercase tracking-wider text-zinc-400 font-semibold block">
                            Enlace directo para el usuario:
                          </label>
                          <div className="flex items-center gap-2">
                            <input
                              type="text"
                              readOnly
                              value={userModal.result?.reset_url || ""}
                              className="bg-black/60 border border-white/10 rounded-lg px-3 py-2 text-xs text-rose-300 font-mono flex-1 outline-none select-all"
                            />
                            <button
                              onClick={() => {
                                navigator.clipboard.writeText(userModal.result?.reset_url || "");
                                toast.success("¡Enlace copiado al portapapeles!");
                              }}
                              className="bg-[#E11D48] hover:bg-[#BE123C] text-white px-3 py-2 rounded-lg text-xs font-semibold flex items-center gap-1 shrink-0 transition-colors"
                            >
                              <Copy size={13} /> Copiar
                            </button>
                          </div>
                        </div>

                        <p className="text-[11px] text-zinc-400 leading-relaxed">
                          {userModal.result?.email_sent
                            ? "✓ Se ha enviado automáticamente una copia de este enlace al correo del usuario."
                            : "⚠ Amazon SES no está configurado para envío automático de correos transaccionales; por favor copia y entrega el enlace manualmente al usuario por WhatsApp o soporte."}
                        </p>
                      </div>
                    )}

                    {userModal.type === "temp_password" && (
                      <div className="space-y-3">
                        <div className="p-3.5 bg-zinc-900 border border-white/10 rounded-xl space-y-2">
                          <label className="text-[10px] uppercase tracking-wider text-zinc-400 font-semibold block">
                            Nueva contraseña provisional:
                          </label>
                          <div className="flex items-center gap-2">
                            <input
                              type="text"
                              readOnly
                              value={userModal.result?.temp_password || ""}
                              className="bg-black/60 border border-emerald-500/30 text-emerald-400 rounded-lg px-3 py-2 text-sm font-mono font-bold flex-1 outline-none select-all"
                            />
                            <button
                              onClick={() => {
                                navigator.clipboard.writeText(userModal.result?.temp_password || "");
                                toast.success("¡Contraseña copiada al portapapeles!");
                              }}
                              className="bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-2 rounded-lg text-xs font-semibold flex items-center gap-1 shrink-0 transition-colors"
                            >
                              <Copy size={13} /> Copiar
                            </button>
                          </div>
                        </div>
                        <p className="text-[11px] text-zinc-400 leading-relaxed">
                          Todas las sesiones previas del usuario fueron revocadas. La próxima vez que inicie sesión con esta contraseña provisional, el sistema le solicitará cambiarla obligatoriamente.
                        </p>
                      </div>
                    )}

                    <div className="pt-2 flex justify-end">
                      <button
                        onClick={() => setUserModal({ isOpen: false, user: null, result: null, type: null })}
                        className="bg-zinc-800 hover:bg-zinc-700 text-white px-4 py-2 rounded-lg text-xs font-semibold transition-colors"
                      >
                        Cerrar
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {usersTotal > 50 && (
              <div className="flex items-center justify-between mt-4">
                <button data-testid="users-prev" disabled={userSkip === 0} onClick={() => setUserSkip(Math.max(0, userSkip - 50))} className="text-sm text-zinc-400 hover:text-white disabled:opacity-30">← Anteriores</button>
                <span className="text-xs text-zinc-500">{userSkip + 1}–{Math.min(userSkip + 50, usersTotal)} de {usersTotal}</span>
                <button data-testid="users-next" disabled={userSkip + 50 >= usersTotal} onClick={() => setUserSkip(userSkip + 50)} className="text-sm text-zinc-400 hover:text-white disabled:opacity-30">Siguientes →</button>
              </div>
            )}
          </div>
        )}
        {tab === "comments" && (
          <div>
            <div className="flex items-center gap-3 mb-2">
              <MessageSquare size={22} className="text-[#E11D48]" />
              <h1 className="font-heading text-2xl">Comentarios</h1>
            </div>
            <p className="text-zinc-400 text-sm mb-6">Modera los comentarios de los usuarios. {commentsTotal} en total.</p>
            <div className="border border-white/10 divide-y divide-white/10" data-testid="admin-comments-list">
              {commentsList.map((c) => (
                <div key={c.id} className="flex items-start gap-4 p-4" data-testid={`admin-comment-${c.id}`}>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm">
                      <span className="font-medium">{c.user_name}</span>
                      <span className="text-zinc-500 text-xs"> · {new Date(c.created_at).toLocaleString()}</span>
                    </p>
                    <p className="text-xs text-zinc-500 mt-0.5">
                      {c.movie_slug ? <Link to={`/titulo/${c.movie_slug}`} className="hover:text-[#E11D48]">{c.movie_title}</Link> : c.movie_title}
                    </p>
                    <p className="text-sm text-zinc-300 mt-2 whitespace-pre-wrap break-words">{c.text}</p>
                  </div>
                  <button data-testid={`admin-delete-comment-${c.id}`} onClick={() => delComment(c.id)} className="text-zinc-400 hover:text-[#E11D48] p-2 shrink-0"><Trash2 size={16} /></button>
                </div>
              ))}
              {commentsList.length === 0 && <div className="p-6 text-zinc-500 text-sm">Aún no hay comentarios.</div>}
            </div>
            {commentsTotal > 50 && (
              <div className="flex items-center justify-between mt-4">
                <button data-testid="comments-prev" disabled={commentsSkip === 0} onClick={() => setCommentsSkip(Math.max(0, commentsSkip - 50))} className="text-sm text-zinc-400 hover:text-white disabled:opacity-30">← Anteriores</button>
                <span className="text-xs text-zinc-500">{commentsSkip + 1}–{Math.min(commentsSkip + 50, commentsTotal)} de {commentsTotal}</span>
                <button data-testid="comments-next" disabled={commentsSkip + 50 >= commentsTotal} onClick={() => setCommentsSkip(commentsSkip + 50)} className="text-sm text-zinc-400 hover:text-white disabled:opacity-30">Siguientes →</button>
              </div>
            )}
          </div>
        )}
        {tab === "import" && (
          <div className="max-w-3xl space-y-10">
            {/* SECCIÓN 1: IMPORTAR DESDE OTRA BASE DE DATOS / SITIO WEB */}
            <div className="border border-white/10 p-6 bg-zinc-900/40 rounded-lg">
              <div className="flex items-center gap-3 mb-2">
                <Database size={22} className="text-[#E11D48]" />
                <h1 className="font-heading text-2xl">Importar desde sitio web online o base de datos anterior</h1>
              </div>
              <p className="text-zinc-400 text-sm mb-6">
                Importa tus películas de tu sitio online o desde una copia de seguridad en Excel, CSV o JSON para conservar todo el contenido y las fotos sin perder nada.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Opción A: Archivo */}
                <div className="p-4 bg-zinc-950 border border-white/10 rounded-lg flex flex-col justify-between">
                  <div>
                    <strong className="text-white text-sm block mb-1 flex items-center gap-2">
                      <FileSpreadsheet size={16} className="text-rose-400" /> Excel / CSV / JSON
                    </strong>
                    <p className="text-xs text-zinc-400 mb-4">
                      Carga un archivo con tus películas. Mapea títulos, descripciones, pósters y tráilers automáticamente.
                    </p>
                  </div>
                  <label className="cursor-pointer inline-flex items-center justify-center gap-2 bg-[#E11D48] text-white px-4 py-2.5 text-xs font-semibold hover:bg-[#BE123C] transition-colors rounded">
                    {importingMovies ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                    {importingMovies ? "Procesando..." : "Subir Archivo de Películas"}
                    <input
                      type="file"
                      accept=".xlsx,.xls,.csv,.json,.txt"
                      className="hidden"
                      onChange={importMoviesFromFile}
                      disabled={importingMovies}
                      data-testid="movies-tab-file-input"
                    />
                  </label>
                </div>

                {/* Opción B: URL */}
                <div className="p-4 bg-zinc-950 border border-white/10 rounded-lg flex flex-col justify-between">
                  <div>
                    <strong className="text-white text-sm block mb-1 flex items-center gap-2">
                      <Globe size={16} className="text-rose-400" /> URL de Sitio Web / API
                    </strong>
                    <p className="text-xs text-zinc-400 mb-3">
                      Ingresa la URL del sitio online o su API (ej. WordPress <code className="text-rose-400">/wp-json/wp/v2/posts</code>).
                    </p>
                  </div>
                  <div className="flex gap-2 mt-2">
                    <input
                      type="url"
                      placeholder="https://lacajalgbt.com"
                      value={importUrl}
                      onChange={(e) => setImportUrl(e.target.value)}
                      className="flex-1 bg-zinc-900 border border-white/15 px-3 py-2 text-xs text-white outline-none focus:border-[#E11D48] rounded min-w-0"
                      data-testid="movies-tab-url-input"
                    />
                    <button
                      onClick={importMoviesFromUrl}
                      disabled={importingMovies || !importUrl.trim()}
                      className="bg-[#E11D48] text-white px-3 py-2 text-xs font-semibold hover:bg-[#BE123C] transition-colors disabled:opacity-50 flex items-center gap-1 rounded shrink-0"
                      data-testid="movies-tab-import-url-button"
                    >
                      {importingMovies ? <Loader2 size={14} className="animate-spin" /> : <Globe size={14} />}
                      Importar
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* SECCIÓN 2: IMPORTAR DESDE TMDB */}
            <div className="border border-white/10 p-6 bg-zinc-900/40 rounded-lg">
              <div className="flex items-center gap-3 mb-2">
                <Clapperboard size={22} className="text-[#E11D48]" />
                <h2 className="font-heading text-xl">Importar catálogo desde TMDb</h2>
              </div>
              <p className="text-zinc-400 text-sm mb-6">Trae películas, series y documentales de temática LGBTQ+ automáticamente (pósters, sinopsis, géneros y tráilers). Cada página trae ~20 títulos.</p>

              <div className="space-y-6">
                <div>
                  <label className="text-xs tracking-wider uppercase text-zinc-400 block mb-2">Contenido</label>
                  <div className="flex gap-2">
                    {[["all", "Todo"], ["movies", "Solo películas"], ["tv", "Solo series"]].map(([v, l]) => (
                      <button key={v} data-testid={`import-media-${v}`} onClick={() => setImpForm({ ...impForm, media: v })} className={`px-4 py-2 text-sm border transition-colors ${impForm.media === v ? "border-[#E11D48] text-[#E11D48] bg-[#E11D48]/10" : "border-white/15 text-zinc-400"}`}>{l}</button>
                    ))}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs tracking-wider uppercase text-zinc-400">Páginas de películas (máx 351)</label>
                    <input data-testid="import-movies-pages" type="number" value={impForm.movies_pages} onChange={(e) => setImpForm({ ...impForm, movies_pages: parseInt(e.target.value) || 0 })} className="w-full bg-transparent border-b border-white/20 py-2 mt-1 outline-none focus:border-[#E11D48]" />
                  </div>
                  <div>
                    <label className="text-xs tracking-wider uppercase text-zinc-400">Páginas de series (máx 48)</label>
                    <input data-testid="import-tv-pages" type="number" value={impForm.tv_pages} onChange={(e) => setImpForm({ ...impForm, tv_pages: parseInt(e.target.value) || 0 })} className="w-full bg-transparent border-b border-white/20 py-2 mt-1 outline-none focus:border-[#E11D48]" />
                  </div>
                </div>

                {imp?.running ? (
                  <div className="flex items-center gap-3 text-[#E11D48]" data-testid="import-running">
                    <Loader2 size={18} className="animate-spin" />
                    <span>Importando... {imp.imported || 0} títulos cargados</span>
                  </div>
                ) : (
                  <button data-testid="start-import-button" onClick={startImport} className="bg-[#E11D48] text-white px-6 py-3 font-medium hover:bg-[#BE123C] transition-colors flex items-center gap-2 rounded">
                    <Download size={18} /> Iniciar importación TMDb
                  </button>
                )}

                {imp && imp.finished && !imp.running && (
                  <p className="text-sm text-emerald-400" data-testid="import-finished">✓ Última importación completada: {imp.imported} títulos.</p>
                )}
                {imp?.error && <p className="text-sm text-[#E11D48]">Error: {imp.error}</p>}
              </div>
              <p className="text-xs text-zinc-500 mt-4">Sugerencia: 351 páginas de películas ≈ 7.000 títulos. Puedes empezar con 60 y luego traer más; los duplicados se omiten automáticamente.</p>
            </div>
          </div>
        )}

        {tab === "ads" && (
          <div className="max-w-2xl">
            <div className="flex items-center gap-3 mb-2">
              <Megaphone size={22} className="text-[#E11D48]" />
              <h1 className="font-heading text-2xl">Publicidad en video (pre-roll)</h1>
            </div>
            <p className="text-zinc-400 text-sm mb-8">Estos videos se reproducen ANTES del tráiler de cada título. Google AdSense sigue funcionando en los banners fijos del sitio. Deja la lista vacía para reproducir el tráiler directamente.</p>

            <div className="border border-white/10 p-6 space-y-6">
              <div>
                <label className="text-xs tracking-wider uppercase text-zinc-400 block mb-2">URLs de video (una por línea · YouTube o enlace .mp4)</label>
                <textarea
                  data-testid="ads-preroll-input"
                  value={adsForm.preroll_text}
                  onChange={(e) => setAdsForm({ ...adsForm, preroll_text: e.target.value })}
                  rows={6}
                  placeholder={"https://www.youtube.com/watch?v=XXXX\nhttps://midominio.com/anuncio.mp4"}
                  className="w-full bg-transparent border border-white/15 p-3 outline-none focus:border-[#E11D48] resize-none text-sm font-mono"
                />
                <p className="text-xs text-zinc-500 mt-1">Si pones varias, se elegirá una al azar en cada reproducción.</p>
              </div>
              <div className="grid grid-cols-2 gap-4 items-end">
                <div>
                  <label className="text-xs tracking-wider uppercase text-zinc-400">Segundos antes de "Saltar anuncio"</label>
                  <input data-testid="ads-skip-input" type="number" min="0" value={adsForm.skip_seconds} onChange={(e) => setAdsForm({ ...adsForm, skip_seconds: e.target.value })} className="w-full bg-transparent border-b border-white/20 py-2 mt-1 outline-none focus:border-[#E11D48]" />
                </div>
                <label className="flex items-center gap-3 cursor-pointer pb-2">
                  <input data-testid="ads-enabled-input" type="checkbox" checked={adsForm.enabled} onChange={(e) => setAdsForm({ ...adsForm, enabled: e.target.checked })} className="w-4 h-4 accent-[#E11D48]" />
                  <span className="text-sm">Anuncios activados</span>
                </label>
              </div>
              <button data-testid="save-ads-button" onClick={saveAds} className="bg-[#E11D48] text-white px-6 py-3 font-medium hover:bg-[#BE123C] transition-colors">Guardar publicidad</button>
            </div>
          </div>
        )}

        {tab === "revista" && (
          <div>
            <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
              <div>
                <h1 className="font-heading text-2xl font-bold flex items-center gap-2">
                  <BookOpen className="text-[#E11D48]" size={24} /> Revista & Publicaciones Editoriales
                </h1>
                <p className="text-xs text-zinc-400 mt-1">
                  Administra las publicaciones de la revista digital, aprueba borradores o crea nuevos artículos.
                </p>
              </div>
              <Link
                to="/revista-editor"
                data-testid="admin-open-revista-editor"
                className="bg-[#E11D48] text-white px-5 py-2.5 rounded-lg text-sm font-semibold flex items-center gap-2 hover:bg-[#BE123C] transition-colors shadow-md"
              >
                <Plus size={16} /> Redactar / Editar Artículos
              </Link>
            </div>

            {/* Status Filter Pills */}
            <div className="flex flex-wrap gap-2 mb-6" data-testid="admin-revista-filters">
              {[["", "Todos"], ["pending", "En revisión"], ["published", "Publicados"], ["draft", "Borradores"], ["rejected", "Rechazados"]].map(([v, l]) => (
                <button
                  key={v || "all"}
                  data-testid={`admin-revista-filter-${v || "all"}`}
                  onClick={() => setAdminArticleFilter(v)}
                  className={`px-4 py-2 text-xs font-medium border rounded-full transition-colors ${
                    adminArticleFilter === v
                      ? "border-[#E11D48] text-[#E11D48] bg-[#E11D48]/10 font-bold"
                      : "border-white/15 text-zinc-400 hover:text-white"
                  }`}
                >
                  {l}
                </button>
              ))}
            </div>

            {/* Articles Table */}
            <div className="border border-white/10 rounded-xl overflow-hidden divide-y divide-white/10 bg-[#0A0A0C]" data-testid="admin-revista-list">
              {adminArticles.map((art) => (
                <div key={art.id} className="flex flex-wrap sm:flex-nowrap items-center gap-4 p-4 hover:bg-white/[0.02]" data-testid={`admin-revista-item-${art.id}`}>
                  {art.cover_url ? (
                    <img src={art.cover_url} alt="" className="w-16 h-10 object-cover rounded bg-zinc-800 shrink-0" />
                  ) : (
                    <div className="w-16 h-10 bg-zinc-800 rounded shrink-0 flex items-center justify-center text-[10px] text-zinc-500">Sin foto</div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm text-zinc-100 truncate">{art.title}</p>
                    <div className="flex flex-wrap items-center gap-2 text-xs text-zinc-500 mt-1">
                      <span className="text-zinc-400 font-medium">{art.category}</span>
                      <span>•</span>
                      <span>Por: {art.author_name || "Redactor"}</span>
                      <span>•</span>
                      <span className={`font-semibold capitalize ${
                        art.status === "published" ? "text-emerald-400" :
                        art.status === "pending" ? "text-amber-400" :
                        art.status === "rejected" ? "text-rose-400" : "text-zinc-400"
                      }`}>
                        {art.status === "published" ? "Publicado" : art.status === "pending" ? "En revisión" : art.status === "rejected" ? "Rechazado" : "Borrador"}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 shrink-0 ml-auto">
                    {art.status === "published" && (
                      <Link
                        to={`/revista/${art.slug}`}
                        target="_blank"
                        className="p-2 text-zinc-400 hover:text-white transition-colors"
                        title="Ver en la revista"
                      >
                        <Eye size={16} />
                      </Link>
                    )}

                    {art.status === "pending" && (
                      <>
                        <button
                          onClick={async () => {
                            await api.post(`/admin/articles/${art.id}/approve`);
                            toast.success("Artículo aprobado y publicado");
                            loadAdminArticles();
                          }}
                          className="px-3 py-1 bg-emerald-600/20 text-emerald-400 border border-emerald-500/30 rounded text-xs font-semibold hover:bg-emerald-600 hover:text-white transition-colors flex items-center gap-1"
                        >
                          <Check size={14} /> Aprobar
                        </button>
                        <button
                          onClick={async () => {
                            await api.post(`/admin/articles/${art.id}/reject`);
                            toast.success("Artículo rechazado");
                            loadAdminArticles();
                          }}
                          className="px-3 py-1 bg-rose-600/20 text-rose-400 border border-rose-500/30 rounded text-xs font-semibold hover:bg-rose-600 hover:text-white transition-colors flex items-center gap-1"
                        >
                          <XCircle size={14} /> Rechazar
                        </button>
                      </>
                    )}

                    <Link
                      to="/revista-editor"
                      className="p-2 text-zinc-400 hover:text-white transition-colors"
                      title="Editar artículo"
                    >
                      <Pencil size={16} />
                    </Link>

                    <button
                      onClick={async () => {
                        if (!window.confirm("¿Eliminar este artículo?")) return;
                        await api.delete(`/articles/${art.id}`);
                        toast.success("Artículo eliminado");
                        loadAdminArticles();
                      }}
                      className="p-2 text-zinc-400 hover:text-[#E11D48] transition-colors"
                      title="Eliminar"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              ))}

              {adminArticles.length === 0 && (
                <div className="p-8 text-center text-zinc-500 text-sm">
                  No se encontraron artículos en este filtro.
                </div>
              )}
            </div>
          </div>
        )}

        {tab === "festivals" && <FestivalsAdminTab />}

        {tab === "newsletter" && (
          <div>
            <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
              <div>
                <div className="flex items-center gap-3">
                  <Mail size={26} className="text-[#E11D48]" />
                  <h1 className="font-heading text-2xl font-bold">Email Marketing & Amazon SES</h1>
                  <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${sesConfig.status === "configured" ? "bg-emerald-500/20 text-emerald-400 border border-emerald-500/30" : "bg-amber-500/20 text-amber-400 border border-amber-500/30"}`}>
                    {sesConfig.status === "configured" ? "✓ Amazon SES Conectado" : "⚠ Modo Simulación (AWS Key requerida)"}
                  </span>
                </div>
                <p className="text-zinc-400 text-sm mt-1">Gestiona correos de bienvenida, boletines de noticias y campañas masivas con Amazon SES.</p>
              </div>

              {/* Casilla rápida de Correo de Prueba */}
              <div className="flex items-center gap-2 bg-zinc-900 border border-white/10 p-2 rounded-xl">
                <input
                  type="email"
                  placeholder="tu-correo@prueba.com"
                  value={testingEmail}
                  onChange={(e) => setTestingEmail(e.target.value)}
                  className="bg-black/60 border border-zinc-700 text-xs text-white px-3 py-1.5 rounded-lg outline-none focus:border-[#E11D48] w-52"
                />
                <button
                  onClick={handleTestSesConnection}
                  disabled={testingConnection}
                  className="bg-zinc-800 hover:bg-zinc-700 text-xs text-white font-medium px-3 py-1.5 rounded-lg transition-colors flex items-center gap-1.5"
                >
                  {testingConnection ? <Loader2 size={13} className="animate-spin" /> : <SendHorizontal size={13} />}
                  Probar SES
                </button>
              </div>
            </div>

            {/* Sub-tabs Navigation */}
            <div className="flex flex-wrap gap-2 border-b border-white/10 pb-3 mb-6">
              {[
                { id: "campaigns", label: "Campañas / Newsletter", icon: Send },
                { id: "welcome", label: "Correo de Bienvenida", icon: CheckCircle2 },
                { id: "ses", label: "Servidor / SMTP / SES", icon: Server },
                { id: "subscribers", label: `Suscriptores (${subscribersList.length})`, icon: UserCheck },
                { id: "logs", label: "Historial / Logs", icon: BarChart3 }
              ].map((st) => (
                <button
                  key={st.id}
                  onClick={() => setNewsletterSubTab(st.id)}
                  className={`flex items-center gap-2 px-4 py-2 text-xs font-semibold rounded-lg transition-colors ${newsletterSubTab === st.id ? "bg-[#E11D48] text-white" : "bg-zinc-900 text-zinc-400 hover:text-white"}`}
                >
                  <st.icon size={14} /> {st.label}
                </button>
              ))}
            </div>

            {/* SUB-TAB 1: CAMPAÑAS */}
            {newsletterSubTab === "campaigns" && (
              <div className="space-y-6">
                <div className="flex justify-between items-center bg-zinc-900/60 p-4 border border-white/10 rounded-xl">
                  <div>
                    <h3 className="text-sm font-bold text-white">Campañas de Newsletter</h3>
                    <p className="text-xs text-zinc-400">Crea y envía campañas masivas a tus usuarios y suscriptores.</p>
                  </div>
                  <button
                    onClick={() => setEditingCampaign({ id: null, title: "", subject: "", recipient_type: "all", html_content: "" })}
                    className="bg-[#E11D48] hover:bg-[#be123c] text-white px-4 py-2 rounded-lg text-xs font-semibold flex items-center gap-2 transition-colors"
                  >
                    <Plus size={14} /> Nueva Campaña
                  </button>
                </div>

                {/* Formulario de Campaña Modal / Box */}
                {editingCampaign && (
                  <div className="bg-zinc-900 border border-[#E11D48]/40 p-6 rounded-2xl space-y-4 shadow-2xl">
                    <div className="flex justify-between items-center border-b border-zinc-800 pb-3">
                      <h3 className="text-base font-bold text-white flex items-center gap-2">
                        <Pencil size={16} className="text-[#E11D48]" />
                        {editingCampaign.id ? "Editar Campaña" : "Crear Nueva Campaña"}
                      </h3>
                      <button onClick={() => setEditingCampaign(null)} className="text-zinc-400 hover:text-white"><X size={18} /></button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="text-xs text-zinc-400 font-semibold block mb-1">Nombre Interno (Solo visible para ti)</label>
                        <input
                          type="text"
                          placeholder="Ej: Estrenos de Fin de Semana #12"
                          value={editingCampaign.title}
                          onChange={(e) => setEditingCampaign({ ...editingCampaign, title: e.target.value })}
                          className="w-full bg-black/60 border border-zinc-700 px-3 py-2 text-xs text-white rounded-lg outline-none focus:border-[#E11D48]"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-zinc-400 font-semibold block mb-1">Destinatarios</label>
                        <select
                          value={editingCampaign.recipient_type}
                          onChange={(e) => setEditingCampaign({ ...editingCampaign, recipient_type: e.target.value })}
                          className="w-full bg-black/60 border border-zinc-700 px-3 py-2 text-xs text-white rounded-lg outline-none focus:border-[#E11D48]"
                        >
                          <option value="all">Todos (Usuarios Registrados + Suscriptores)</option>
                          <option value="users">Solo Usuarios Registrados</option>
                          <option value="subscribers">Solo Suscriptores de Boletín</option>
                        </select>
                      </div>
                    </div>

                    <div>
                      <label className="text-xs text-zinc-400 font-semibold block mb-1">Asunto del Correo (Soporta etiqueta &#123;NAME&#125;)</label>
                      <input
                        type="text"
                        placeholder="Ej: ¡Nuevas películas LGBTQ+ disponibles para ti, {NAME}! 🎬"
                        value={editingCampaign.subject}
                        onChange={(e) => setEditingCampaign({ ...editingCampaign, subject: e.target.value })}
                        className="w-full bg-black/60 border border-zinc-700 px-3 py-2 text-xs text-white rounded-lg outline-none focus:border-[#E11D48]"
                      />
                    </div>

                    <div>
                      <div className="flex justify-between items-center mb-1">
                        <label className="text-xs text-zinc-400 font-semibold">Contenido HTML del Mensaje</label>
                        <span className="text-[11px] text-zinc-500">Etiquetas disponibles: <code className="text-rose-400">&#123;NAME&#125;</code>, <code className="text-rose-400">&#123;EMAIL&#125;</code>, <code className="text-rose-400">&#123;SITE_URL&#125;</code>, <code className="text-rose-400">&#123;UNSUBSCRIBE_URL&#125;</code></span>
                      </div>
                      <textarea
                        rows={10}
                        placeholder="<h2>¡Hola {NAME}!</h2><p>Te traemos las últimas novedades...</p>"
                        value={editingCampaign.html_content}
                        onChange={(e) => setEditingCampaign({ ...editingCampaign, html_content: e.target.value })}
                        className="w-full bg-black/60 border border-zinc-700 p-3 text-xs text-white font-mono rounded-lg outline-none focus:border-[#E11D48]"
                      />
                    </div>

                    <div className="flex justify-end gap-3 pt-2">
                      <button
                        type="button"
                        onClick={() => setEditingCampaign(null)}
                        className="px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 text-xs font-semibold rounded-lg"
                      >
                        Cancelar
                      </button>
                      <button
                        type="button"
                        onClick={handleSaveCampaign}
                        className="px-5 py-2 bg-[#E11D48] hover:bg-[#be123c] text-white text-xs font-semibold rounded-lg transition-colors"
                      >
                        Guardar Campaña
                      </button>
                    </div>
                  </div>
                )}

                {/* Lista de Campañas */}
                <div className="grid grid-cols-1 gap-4">
                  {campaignsList.map((c) => (
                    <div key={c.id} className="bg-zinc-900 border border-zinc-800 p-5 rounded-xl flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <h4 className="text-sm font-bold text-white">{c.title}</h4>
                          <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold uppercase ${c.status === "sent" ? "bg-emerald-500/20 text-emerald-400 border border-emerald-500/30" : c.status === "sending" ? "bg-blue-500/20 text-blue-400 border border-blue-500/30" : "bg-zinc-800 text-zinc-400"}`}>
                            {c.status === "sent" ? "Enviado" : c.status === "sending" ? "Enviando..." : "Borrador"}
                          </span>
                        </div>
                        <p className="text-xs text-zinc-400 font-mono">Asunto: "{c.subject}"</p>
                        <p className="text-[11px] text-zinc-500">
                          Destinatarios: {c.recipient_type === "all" ? "Todos" : c.recipient_type === "users" ? "Solo Usuarios" : "Solo Suscriptores"} •
                          Enviados: <strong className="text-emerald-400">{c.sent_count || 0}</strong> •
                          Errores: <strong className="text-rose-400">{c.failed_count || 0}</strong> •
                          Creado: {new Date(c.created_at).toLocaleDateString()}
                        </p>
                      </div>

                      <div className="flex items-center gap-2 flex-wrap">
                        <button
                          onClick={() => handleTestSendCampaign(c.id)}
                          disabled={sendingTest}
                          className="px-3 py-1.5 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 text-xs font-semibold rounded-lg flex items-center gap-1"
                          title="Envía un correo de prueba a la dirección especificada en la barra superior"
                        >
                          <SendHorizontal size={13} /> Enviar Prueba
                        </button>
                        <button
                          onClick={() => handleSendCampaign(c.id)}
                          disabled={sendingCampaignId === c.id}
                          className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-semibold rounded-lg flex items-center gap-1 transition-colors"
                        >
                          {sendingCampaignId === c.id ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
                          Enviar Campaña (SES)
                        </button>
                        <button
                          onClick={() => setEditingCampaign(c)}
                          className="p-2 text-zinc-400 hover:text-white transition-colors"
                          title="Editar"
                        >
                          <Pencil size={15} />
                        </button>
                        <button
                          onClick={() => handleDeleteCampaign(c.id)}
                          className="p-2 text-zinc-400 hover:text-rose-400 transition-colors"
                          title="Eliminar"
                        >
                          <Trash2 size={15} />
                        </button>
                      </div>
                    </div>
                  ))}

                  {campaignsList.length === 0 && (
                    <div className="p-12 text-center text-zinc-500 text-xs bg-zinc-900/40 border border-zinc-800/80 rounded-2xl">
                      No hay campañas creadas aún. Haz clic en "Nueva Campaña" para redactar tu primera newsletter.
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* SUB-TAB 2: CORREO DE BIENVENIDA */}
            {newsletterSubTab === "welcome" && (
              <form onSubmit={handleSaveWelcome} className="bg-zinc-900 border border-zinc-800 p-6 rounded-2xl space-y-5">
                <div className="flex justify-between items-center border-b border-zinc-800 pb-3">
                  <div>
                    <h3 className="text-base font-bold text-white flex items-center gap-2">
                      <CheckCircle2 className="text-[#E11D48]" size={18} /> Correo Automático de Bienvenida
                    </h3>
                    <p className="text-xs text-zinc-400">Este mensaje se envía automáticamente al registrarse un usuario o suscribirse al boletín.</p>
                  </div>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={welcomeConfig.enabled}
                      onChange={(e) => setWelcomeConfig({ ...welcomeConfig, enabled: e.target.checked })}
                      className="w-4 h-4 accent-[#E11D48]"
                    />
                    <span className="text-xs text-white font-semibold">Envío Automático Activado</span>
                  </label>
                </div>

                <div>
                  <label className="text-xs text-zinc-400 font-semibold block mb-1">Asunto del Correo de Bienvenida</label>
                  <input
                    type="text"
                    value={welcomeConfig.subject}
                    onChange={(e) => setWelcomeConfig({ ...welcomeConfig, subject: e.target.value })}
                    className="w-full bg-black/60 border border-zinc-700 px-3 py-2 text-xs text-white rounded-lg outline-none focus:border-[#E11D48]"
                  />
                </div>

                <div>
                  <label className="text-xs text-zinc-400 font-semibold block mb-1">Plantilla HTML del Mensaje</label>
                  <textarea
                    rows={12}
                    value={welcomeConfig.html}
                    onChange={(e) => setWelcomeConfig({ ...welcomeConfig, html: e.target.value })}
                    className="w-full bg-black/60 border border-zinc-700 p-3 text-xs text-white font-mono rounded-lg outline-none focus:border-[#E11D48]"
                  />
                </div>

                <div className="flex justify-end">
                  <button
                    type="submit"
                    disabled={savingWelcome}
                    className="bg-[#E11D48] hover:bg-[#be123c] text-white px-6 py-2.5 rounded-xl text-xs font-semibold flex items-center gap-2 transition-colors disabled:opacity-50"
                  >
                    {savingWelcome ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                    Guardar Plantilla de Bienvenida
                  </button>
                </div>
              </form>
            )}

            {/* SUB-TAB 3: CONFIGURACIÓN DE CORREO (SMTP / AMAZON SES) */}
            {newsletterSubTab === "ses" && (
              <form onSubmit={handleSaveSes} className="bg-zinc-900 border border-zinc-800 p-6 rounded-2xl space-y-6">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between border-b border-zinc-800 pb-4 gap-4">
                  <div>
                    <h3 className="text-base font-bold text-white flex items-center gap-2">
                      <Server size={18} className="text-[#E11D48]" /> Configuración del Servidor de Correo (SMTP / Amazon SES)
                    </h3>
                    <p className="text-xs text-zinc-400 mt-0.5">Configura el servicio con el que la plataforma enviará notificaciones, boletines y aprobaciones.</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-1.5 border ${
                      sesConfig.status === "configured_smtp" || (sesConfig.provider === "smtp" && sesConfig.smtp_host && sesConfig.hasSmtpPass)
                        ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/30"
                        : sesConfig.status === "configured_ses" || (sesConfig.provider === "aws_ses" && sesConfig.hasAccessKey)
                        ? "bg-blue-500/10 text-blue-400 border-blue-500/30"
                        : "bg-amber-500/10 text-amber-400 border-amber-500/30"
                    }`}>
                      <span className="w-2 h-2 rounded-full animate-pulse bg-current"></span>
                      {sesConfig.provider === "smtp"
                        ? (sesConfig.smtp_host ? `SMTP Activo (${sesConfig.smtp_host})` : "SMTP Sin Configurar")
                        : (sesConfig.hasAccessKey ? `AWS SES Activo (${sesConfig.region})` : "AWS SES Sin Configurar")}
                    </span>
                  </div>
                </div>

                {/* Proveedor de Correo */}
                <div className="bg-black/40 border border-zinc-800 p-4 rounded-xl space-y-4">
                  <label className="text-xs text-zinc-300 font-semibold uppercase tracking-wider block">1. Selecciona el Proveedor de Envío</label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => setSesConfig({ ...sesConfig, provider: "smtp" })}
                      className={`p-4 border rounded-xl text-left transition-all flex items-start gap-3 ${
                        (sesConfig.provider || "smtp") === "smtp"
                          ? "bg-[#E11D48]/15 border-[#E11D48] text-white"
                          : "bg-zinc-900 border-zinc-800 text-zinc-400 hover:text-white"
                      }`}
                    >
                      <Mail size={22} className={sesConfig.provider === "smtp" ? "text-[#E11D48]" : "text-zinc-500"} />
                      <div>
                        <div className="text-xs font-bold text-white">Servidor SMTP (Recomendado)</div>
                        <div className="text-[11px] text-zinc-400 mt-0.5">Compatible con Gmail, Hostinger, Brevo (Sendinblue), Mailtrap, cPanel y cualquier webmail.</div>
                      </div>
                    </button>

                    <button
                      type="button"
                      onClick={() => setSesConfig({ ...sesConfig, provider: "aws_ses" })}
                      className={`p-4 border rounded-xl text-left transition-all flex items-start gap-3 ${
                        sesConfig.provider === "aws_ses"
                          ? "bg-[#E11D48]/15 border-[#E11D48] text-white"
                          : "bg-zinc-900 border-zinc-800 text-zinc-400 hover:text-white"
                      }`}
                    >
                      <Server size={22} className={sesConfig.provider === "aws_ses" ? "text-[#E11D48]" : "text-zinc-500"} />
                      <div>
                        <div className="text-xs font-bold text-white">Amazon SES (AWS)</div>
                        <div className="text-[11px] text-zinc-400 mt-0.5">Servicio institucional en la nube de Amazon Web Services (requiere AWS Access Keys).</div>
                      </div>
                    </button>
                  </div>
                </div>

                {/* Configuración SMTP */}
                {(sesConfig.provider || "smtp") === "smtp" && (
                  <div className="space-y-4">
                    <div className="flex flex-wrap justify-between items-center bg-zinc-900/80 p-3 rounded-lg border border-zinc-800">
                      <span className="text-xs text-zinc-300 font-medium">Plantillas rápidas de proveedores SMTP:</span>
                      <div className="flex flex-wrap gap-2 mt-2 sm:mt-0">
                        {[
                          { label: "Gmail", key: "gmail" },
                          { label: "Brevo", key: "brevo" },
                          { label: "Hostinger", key: "hostinger" },
                          { label: "cPanel / Webmail", key: "cpanel" },
                          { label: "Mailtrap", key: "mailtrap" }
                        ].map((p) => (
                          <button
                            key={p.key}
                            type="button"
                            onClick={() => {
                              if (p.key === "gmail") setSesConfig(prev => ({ ...prev, smtp_host: "smtp.gmail.com", smtp_port: 587, smtp_secure: false }));
                              if (p.key === "brevo") setSesConfig(prev => ({ ...prev, smtp_host: "smtp-relay.brevo.com", smtp_port: 587, smtp_secure: false }));
                              if (p.key === "hostinger") setSesConfig(prev => ({ ...prev, smtp_host: "smtp.hostinger.com", smtp_port: 465, smtp_secure: true }));
                              if (p.key === "cpanel") setSesConfig(prev => ({ ...prev, smtp_host: "mail." + (sesConfig.fromEmail?.split("@")[1] || "midominio.com"), smtp_port: 465, smtp_secure: true }));
                              if (p.key === "mailtrap") setSesConfig(prev => ({ ...prev, smtp_host: "sandbox.smtp.mailtrap.io", smtp_port: 2525, smtp_secure: false }));
                            }}
                            className="bg-black/60 hover:bg-zinc-800 border border-zinc-700 text-zinc-300 hover:text-white px-2.5 py-1 rounded text-[11px] transition-colors"
                          >
                            + {p.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="text-xs text-zinc-400 font-semibold block mb-1">Servidor Host SMTP *</label>
                        <input
                          type="text"
                          placeholder="smtp.gmail.com, smtp.hostinger.com o smtp-relay.brevo.com"
                          value={sesConfig.smtp_host || ""}
                          onChange={(e) => setSesConfig({ ...sesConfig, smtp_host: e.target.value })}
                          className="w-full bg-black/60 border border-zinc-700 px-3 py-2 text-xs text-white font-mono rounded-lg outline-none focus:border-[#E11D48]"
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="text-xs text-zinc-400 font-semibold block mb-1">Puerto SMTP *</label>
                          <input
                            type="number"
                            placeholder="587 o 465"
                            value={sesConfig.smtp_port || 587}
                            onChange={(e) => setSesConfig({ ...sesConfig, smtp_port: parseInt(e.target.value, 10) || 587 })}
                            className="w-full bg-black/60 border border-zinc-700 px-3 py-2 text-xs text-white font-mono rounded-lg outline-none focus:border-[#E11D48]"
                          />
                        </div>
                        <div className="flex items-end pb-2">
                          <label className="flex items-center gap-2 cursor-pointer text-xs text-zinc-300">
                            <input
                              type="checkbox"
                              checked={Boolean(sesConfig.smtp_secure)}
                              onChange={(e) => setSesConfig({ ...sesConfig, smtp_secure: e.target.checked })}
                              className="accent-[#E11D48] rounded"
                            />
                            Usar SSL (Port 465)
                          </label>
                        </div>
                      </div>

                      <div>
                        <label className="text-xs text-zinc-400 font-semibold block mb-1">Usuario / Correo SMTP *</label>
                        <input
                          type="text"
                          placeholder="tu-correo@midominio.com o tu usuario"
                          value={sesConfig.smtp_user || ""}
                          onChange={(e) => setSesConfig({ ...sesConfig, smtp_user: e.target.value })}
                          className="w-full bg-black/60 border border-zinc-700 px-3 py-2 text-xs text-white font-mono rounded-lg outline-none focus:border-[#E11D48]"
                        />
                      </div>

                      <div>
                        <label className="text-xs text-zinc-400 font-semibold block mb-1">Contraseña o App Password *</label>
                        <input
                          type="password"
                          placeholder={sesConfig.hasSmtpPass ? "•••••••• (Guardada - déjala vacía para conservar)" : "Contraseña SMTP o Contraseña de Aplicación"}
                          value={sesConfig.smtp_pass || ""}
                          onChange={(e) => setSesConfig({ ...sesConfig, smtp_pass: e.target.value })}
                          className="w-full bg-black/60 border border-zinc-700 px-3 py-2 text-xs text-white font-mono rounded-lg outline-none focus:border-[#E11D48]"
                        />
                      </div>
                    </div>

                    <div className="bg-amber-500/10 border border-amber-500/20 p-3 rounded-lg text-amber-200/90 text-xs leading-relaxed">
                      💡 <strong>Nota para usuarios de Gmail:</strong> Debes usar una <strong>Contraseña de Aplicación</strong> de 16 caracteres (generada en tu cuenta Google -&gt; Seguridad -&gt; Verificación en dos pasos -&gt; Contraseñas de aplicación).
                    </div>
                  </div>
                )}

                {/* Configuración AWS SES */}
                {sesConfig.provider === "aws_ses" && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs text-zinc-400 font-semibold block mb-1">Región de AWS SES</label>
                      <select
                        value={sesConfig.region || "us-east-1"}
                        onChange={(e) => setSesConfig({ ...sesConfig, region: e.target.value })}
                        className="w-full bg-black/60 border border-zinc-700 px-3 py-2 text-xs text-white rounded-lg outline-none focus:border-[#E11D48]"
                      >
                        <option value="us-east-1">EE. UU. Este (N. Virginia) - us-east-1</option>
                        <option value="us-east-2">EE. UU. Este (Ohio) - us-east-2</option>
                        <option value="us-west-2">EE. UU. Oeste (Oregón) - us-west-2</option>
                        <option value="eu-west-1">Europa (Irlanda) - eu-west-1</option>
                        <option value="sa-east-1">Sudamérica (São Paulo) - sa-east-1</option>
                      </select>
                    </div>

                    <div>
                      <label className="text-xs text-zinc-400 font-semibold block mb-1">AWS Access Key ID</label>
                      <input
                        type="text"
                        placeholder={sesConfig.hasAccessKey ? `${sesConfig.accessKeyIdPreview || "AKIA••••"} (Guardada - vacía para conservar)` : "AKIAIOSFODNN7EXAMPLE"}
                        value={sesConfig.accessKeyId || ""}
                        onChange={(e) => setSesConfig({ ...sesConfig, accessKeyId: e.target.value })}
                        className="w-full bg-black/60 border border-zinc-700 px-3 py-2 text-xs text-white font-mono rounded-lg outline-none focus:border-[#E11D48]"
                      />
                    </div>

                    <div>
                      <label className="text-xs text-zinc-400 font-semibold block mb-1">AWS Secret Access Key</label>
                      <input
                        type="password"
                        placeholder={sesConfig.hasSecretKey ? "•••••••• (Guardada - vacía para conservar)" : "wJalrXUtnFEMI/K7MDENG/bPxRfiCYEXAMPLEKEY"}
                        value={sesConfig.secretAccessKey || ""}
                        onChange={(e) => setSesConfig({ ...sesConfig, secretAccessKey: e.target.value })}
                        className="w-full bg-black/60 border border-zinc-700 px-3 py-2 text-xs text-white font-mono rounded-lg outline-none focus:border-[#E11D48]"
                      />
                    </div>
                  </div>
                )}

                {/* Detalles de Remitente Comunes */}
                <div className="border-t border-zinc-800 pt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs text-zinc-400 font-semibold block mb-1">Correo Remitente (From Email)</label>
                    <input
                      type="email"
                      placeholder="boletin@lacajalgbt.com"
                      value={sesConfig.fromEmail || ""}
                      onChange={(e) => setSesConfig({ ...sesConfig, fromEmail: e.target.value })}
                      className="w-full bg-black/60 border border-zinc-700 px-3 py-2 text-xs text-white rounded-lg outline-none focus:border-[#E11D48]"
                    />
                  </div>

                  <div>
                    <label className="text-xs text-zinc-400 font-semibold block mb-1">Nombre Visible del Remitente (From Name)</label>
                    <input
                      type="text"
                      placeholder="La Caja LGBT"
                      value={sesConfig.fromName || ""}
                      onChange={(e) => setSesConfig({ ...sesConfig, fromName: e.target.value })}
                      className="w-full bg-black/60 border border-zinc-700 px-3 py-2 text-xs text-white rounded-lg outline-none focus:border-[#E11D48]"
                    />
                  </div>
                </div>

                <div className="flex flex-wrap justify-between items-center gap-3 pt-2 border-t border-zinc-800">
                  <div className="flex items-center gap-2">
                    <input
                      type="email"
                      placeholder="tu-correo@prueba.com"
                      value={testingEmail}
                      onChange={(e) => setTestingEmail(e.target.value)}
                      className="bg-black/60 border border-zinc-700 text-xs text-white px-3 py-2 rounded-lg outline-none focus:border-[#E11D48] w-56"
                    />
                    <button
                      type="button"
                      onClick={handleTestSesConnection}
                      disabled={testingConnection}
                      className="bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 text-xs text-white font-medium px-4 py-2 rounded-lg transition-colors flex items-center gap-1.5 disabled:opacity-50"
                    >
                      {testingConnection ? <Loader2 size={13} className="animate-spin" /> : <SendHorizontal size={13} />}
                      Enviar Correo de Prueba
                    </button>
                  </div>

                  <button
                    type="submit"
                    disabled={savingSes}
                    className="bg-[#E11D48] hover:bg-[#be123c] text-white px-6 py-2.5 rounded-xl text-xs font-semibold flex items-center gap-2 transition-colors disabled:opacity-50"
                  >
                    {savingSes ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                    Guardar Configuración de Correo
                  </button>
                </div>
              </form>
            )}

            {/* SUB-TAB 4: SUSCRIPTORES */}
            {newsletterSubTab === "subscribers" && (
              <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6 space-y-4">
                <div className="flex justify-between items-center border-b border-zinc-800 pb-3">
                  <h3 className="text-base font-bold text-white flex items-center gap-2">
                    <UserCheck size={18} className="text-[#E11D48]" /> Lista Completa de Suscriptores & Usuarios
                  </h3>
                  <p className="text-xs text-zinc-400">Total: {subscribersList.length} receptores</p>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full text-left text-xs">
                    <thead>
                      <tr className="border-b border-zinc-800 text-zinc-400 uppercase">
                        <th className="py-2.5 px-3">Correo</th>
                        <th className="py-2.5 px-3">Nombre</th>
                        <th className="py-2.5 px-3">Origen</th>
                        <th className="py-2.5 px-3">Estado</th>
                        <th className="py-2.5 px-3">Fecha</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-zinc-800/60">
                      {subscribersList.map((s, idx) => (
                        <tr key={idx} className="hover:bg-black/40">
                          <td className="py-2.5 px-3 text-white font-mono">{s.email}</td>
                          <td className="py-2.5 px-3 text-zinc-300">{s.name || "—"}</td>
                          <td className="py-2.5 px-3 text-zinc-400">{s.source}</td>
                          <td className="py-2.5 px-3">
                            <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${s.unsubscribed ? "bg-rose-500/20 text-rose-400" : "bg-emerald-500/20 text-emerald-400"}`}>
                              {s.unsubscribed ? "Dado de baja" : "Activo"}
                            </span>
                          </td>
                          <td className="py-2.5 px-3 text-zinc-500">{new Date(s.created_at).toLocaleDateString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* SUB-TAB 5: LOGS */}
            {newsletterSubTab === "logs" && (
              <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6 space-y-4">
                <div className="flex justify-between items-center border-b border-zinc-800 pb-3">
                  <h3 className="text-base font-bold text-white flex items-center gap-2">
                    <BarChart3 size={18} className="text-[#E11D48]" /> Historial de Envíos SES
                  </h3>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full text-left text-xs">
                    <thead>
                      <tr className="border-b border-zinc-800 text-zinc-400 uppercase">
                        <th className="py-2.5 px-3">Fecha</th>
                        <th className="py-2.5 px-3">Destinatario</th>
                        <th className="py-2.5 px-3">Asunto</th>
                        <th className="py-2.5 px-3">Estado</th>
                        <th className="py-2.5 px-3">Detalle / ID</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-zinc-800/60 font-mono text-[11px]">
                      {emailLogsList.map((log) => (
                        <tr key={log.id} className="hover:bg-black/40">
                          <td className="py-2 px-3 text-zinc-400">{new Date(log.created_at).toLocaleString()}</td>
                          <td className="py-2 px-3 text-white">{log.to}</td>
                          <td className="py-2 px-3 text-zinc-300 font-sans">{log.subject}</td>
                          <td className="py-2 px-3">
                            <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${log.status === "sent" ? "bg-emerald-500/20 text-emerald-400" : log.status === "simulated" ? "bg-amber-500/20 text-amber-400" : "bg-rose-500/20 text-rose-400"}`}>
                              {log.status}
                            </span>
                          </td>
                          <td className="py-2 px-3 text-zinc-500">{log.messageId || log.error || log.message || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {tab === "dmca" && (
          <div className="space-y-6" data-testid="admin-dmca-tab">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <div className="flex items-center gap-2.5">
                  <Scale className="w-6 h-6 text-[#E11D48]" />
                  <h1 className="font-heading text-2xl font-bold">Gestión de Reclamaciones DMCA & Copyright</h1>
                </div>
                <p className="text-xs text-zinc-400 mt-1">
                  Requerimientos formales de retirada y notificaciones de infracción de propiedad intelectual radicados por titulares.
                </p>
              </div>

              <div className="flex items-center gap-3">
                <button
                  onClick={loadDmcaClaims}
                  disabled={loadingDmca}
                  className="px-3.5 py-2 rounded-xl bg-zinc-900 border border-zinc-800 text-xs font-medium text-zinc-300 hover:text-white hover:bg-zinc-800 flex items-center gap-1.5 transition-colors disabled:opacity-50"
                >
                  <RefreshCw className={`w-3.5 h-3.5 ${loadingDmca ? "animate-spin" : ""}`} />
                  <span>Actualizar lista</span>
                </button>
              </div>
            </div>

            {/* Filter pills */}
            <div className="flex flex-wrap gap-2 border-b border-zinc-800 pb-3">
              {[
                { id: "all", label: `Todos (${dmcaClaims.length})` },
                { id: "pending", label: `Pendientes (${dmcaClaims.filter(c => c.status === "pending").length})` },
                { id: "investigating", label: `En Revisión (${dmcaClaims.filter(c => c.status === "investigating").length})` },
                { id: "resolved", label: `Resueltos / Retirados (${dmcaClaims.filter(c => c.status === "resolved").length})` },
                { id: "rejected", label: `Desestimados (${dmcaClaims.filter(c => c.status === "rejected").length})` }
              ].map(f => (
                <button
                  key={f.id}
                  onClick={() => setDmcaFilter(f.id)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                    dmcaFilter === f.id
                      ? "bg-[#E11D48] text-white"
                      : "bg-zinc-900 text-zinc-400 hover:text-zinc-200"
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>

            {/* Claims Table or List */}
            {loadingDmca ? (
              <div className="p-12 text-center text-zinc-500 flex items-center justify-center gap-2">
                <Loader2 className="w-5 h-5 animate-spin text-[#E11D48]" />
                <span className="text-sm">Cargando expedientes de propiedad intelectual...</span>
              </div>
            ) : dmcaClaims.length === 0 ? (
              <div className="p-12 border border-dashed border-zinc-800 rounded-2xl text-center">
                <ShieldCheck className="w-10 h-10 text-emerald-500/80 mx-auto mb-3" />
                <h3 className="text-base font-semibold text-white">Sin reclamaciones pendientes</h3>
                <p className="text-xs text-zinc-400 max-w-md mx-auto mt-1">
                  La plataforma está al día y protegida conforme al Digital Millennium Copyright Act. Todas las solicitudes recibidas aparecerán en este registro auditado.
                </p>
              </div>
            ) : (
              <div className="border border-zinc-800 rounded-2xl overflow-hidden bg-zinc-950/40">
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-xs border-collapse">
                    <thead>
                      <tr className="border-b border-zinc-800 bg-zinc-900/60 text-zinc-400 uppercase text-[10px] tracking-wider">
                        <th className="py-3 px-4">Ticket / Fecha</th>
                        <th className="py-3 px-4">Reclamante / Titular</th>
                        <th className="py-3 px-4">Obra Impugnada</th>
                        <th className="py-3 px-4">Estado</th>
                        <th className="py-3 px-4 text-right">Acciones</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-zinc-800/60 text-zinc-300">
                      {dmcaClaims
                        .filter(c => dmcaFilter === "all" || c.status === dmcaFilter)
                        .map(c => {
                          const statusColor =
                            c.status === "pending"
                              ? "bg-amber-500/20 text-amber-400 border border-amber-500/40"
                              : c.status === "investigating"
                              ? "bg-sky-500/20 text-sky-400 border border-sky-500/40"
                              : c.status === "resolved"
                              ? "bg-emerald-500/20 text-emerald-400 border border-emerald-500/40"
                              : "bg-zinc-800 text-zinc-400 border border-zinc-700";

                          return (
                            <tr key={c.id} className="hover:bg-white/[0.02] transition-colors">
                              <td className="py-3.5 px-4">
                                <span className="font-mono font-bold text-white block">{c.id}</span>
                                <span className="text-[11px] text-zinc-500">{new Date(c.created_at).toLocaleString()}</span>
                              </td>
                              <td className="py-3.5 px-4">
                                <p className="font-medium text-white">{c.full_name}</p>
                                <p className="text-[11px] text-zinc-400">{c.organization}</p>
                                <p className="text-[10px] text-zinc-500 font-mono">{c.email}</p>
                              </td>
                              <td className="py-3.5 px-4 max-w-xs">
                                <p className="font-medium text-zinc-200 truncate">{c.infringing_url_or_title}</p>
                                <p className="text-[11px] text-zinc-400 line-clamp-1">{c.description}</p>
                              </td>
                              <td className="py-3.5 px-4">
                                <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase ${statusColor}`}>
                                  {c.status === "pending" ? "Pendiente" : c.status === "investigating" ? "En Revisión" : c.status === "resolved" ? "Resuelto" : "Desestimado"}
                                </span>
                              </td>
                              <td className="py-3.5 px-4 text-right">
                                <div className="flex items-center justify-end gap-2">
                                  <button
                                    onClick={() => {
                                      setSelectedDmca(c);
                                      setDmcaNote(c.admin_notes || "");
                                    }}
                                    className="px-3 py-1.5 rounded-lg bg-zinc-900 border border-zinc-700 text-zinc-200 hover:text-white hover:bg-zinc-800 text-xs font-medium transition-colors"
                                  >
                                    Ver Expediente
                                  </button>
                                  <button
                                    onClick={() => deleteDmcaClaim(c.id)}
                                    className="p-1.5 rounded-lg text-zinc-500 hover:text-rose-500 transition-colors"
                                    title="Eliminar registro"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Modal for Claim Details and Resolution */}
            {selectedDmca && (
              <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setSelectedDmca(null)}>
                <div
                  className="bg-zinc-950 border border-zinc-800 w-full max-w-2xl p-6 sm:p-8 rounded-2xl shadow-2xl space-y-6 max-h-[90vh] overflow-y-auto"
                  onClick={e => e.stopPropagation()}
                >
                  <div className="flex items-start justify-between gap-4 border-b border-zinc-800 pb-4">
                    <div>
                      <span className="text-[10px] uppercase tracking-widest text-[#E11D48] font-bold">Expediente Legal</span>
                      <h2 className="text-xl font-heading font-semibold text-white mt-0.5 flex items-center gap-2">
                        <span>{selectedDmca.id}</span>
                      </h2>
                      <p className="text-xs text-zinc-400 mt-1">
                        Radicado el {new Date(selectedDmca.created_at).toLocaleString()} · IP: {selectedDmca.ip_address || "Registrada"}
                      </p>
                    </div>
                    <button onClick={() => setSelectedDmca(null)} className="p-1 text-zinc-400 hover:text-white rounded-lg">
                      <X className="w-5 h-5" />
                    </button>
                  </div>

                  {/* Reclamante info */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 bg-zinc-900/60 p-4 rounded-xl border border-zinc-800 text-xs">
                    <div>
                      <p className="text-zinc-500 uppercase text-[10px] font-semibold">Titular / Declarante</p>
                      <p className="font-medium text-white text-sm mt-0.5">{selectedDmca.full_name}</p>
                      <p className="text-zinc-400">{selectedDmca.organization}</p>
                    </div>
                    <div>
                      <p className="text-zinc-500 uppercase text-[10px] font-semibold">Contacto</p>
                      <p className="text-white mt-0.5">Email: <a href={`mailto:${selectedDmca.email}`} className="text-[#E11D48] hover:underline">{selectedDmca.email}</a></p>
                      {selectedDmca.phone && <p className="text-zinc-400">Tel: {selectedDmca.phone}</p>}
                    </div>
                  </div>

                  {/* Content details */}
                  <div className="space-y-3 text-xs">
                    <div className="bg-zinc-900/60 p-4 rounded-xl border border-zinc-800 space-y-2">
                      <p className="text-zinc-500 uppercase text-[10px] font-semibold">Obra / URL impugnada en La Caja LGBT</p>
                      <p className="font-mono text-zinc-200 bg-black/50 p-2.5 rounded-lg border border-zinc-800 break-all select-all">
                        {selectedDmca.infringing_url_or_title}
                      </p>
                    </div>

                    {selectedDmca.original_work_evidence && (
                      <div className="bg-zinc-900/60 p-4 rounded-xl border border-zinc-800 space-y-2">
                        <p className="text-zinc-500 uppercase text-[10px] font-semibold">Prueba de titularidad / Enlace obra original</p>
                        <a
                          href={selectedDmca.original_work_evidence.startsWith("http") ? selectedDmca.original_work_evidence : `https://${selectedDmca.original_work_evidence}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 text-[#E11D48] hover:underline break-all"
                        >
                          <span>{selectedDmca.original_work_evidence}</span>
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      </div>
                    )}

                    <div className="bg-zinc-900/60 p-4 rounded-xl border border-zinc-800 space-y-2">
                      <p className="text-zinc-500 uppercase text-[10px] font-semibold">Descripción del reclamante</p>
                      <p className="text-zinc-300 leading-relaxed whitespace-pre-wrap">{selectedDmca.description}</p>
                    </div>

                    <div className="bg-zinc-900/60 p-4 rounded-xl border border-zinc-800 grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <p className="text-zinc-500 uppercase text-[10px] font-semibold">Firma electrónica</p>
                        <p className="font-mono text-amber-400 font-bold mt-0.5">{selectedDmca.signature}</p>
                      </div>
                      <div>
                        <p className="text-zinc-500 uppercase text-[10px] font-semibold">Declaraciones bajo perjurio</p>
                        <p className="text-emerald-400 font-semibold mt-0.5 flex items-center gap-1">
                          <CheckCircle2 className="w-3.5 h-3.5" />
                          <span>Buena fe y exactitud aceptadas</span>
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Resolution Notes & Actions */}
                  <div className="space-y-3 pt-2 border-t border-zinc-800">
                    <label className="block text-xs font-semibold text-zinc-300">
                      Notas de Resolución y Moderación Interna
                    </label>
                    <textarea
                      rows={2}
                      value={dmcaNote}
                      onChange={e => setDmcaNote(e.target.value)}
                      placeholder="Escribe la justificación o acciones tomadas..."
                      className="w-full bg-black/60 border border-zinc-800 rounded-xl px-3.5 py-2 text-xs text-white placeholder-zinc-500 focus:outline-none focus:border-[#E11D48]"
                    />
                    <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
                      <div className="flex flex-wrap gap-2">
                        <button
                          onClick={() => updateDmcaStatus(selectedDmca.id, "investigating", dmcaNote)}
                          className="px-3 py-2 rounded-xl bg-sky-500/20 text-sky-400 border border-sky-500/40 text-xs font-semibold hover:bg-sky-500/30 transition-colors"
                        >
                          Marcar en Revisión
                        </button>
                        <button
                          onClick={() => updateDmcaStatus(selectedDmca.id, "resolved", dmcaNote)}
                          className="px-3 py-2 rounded-xl bg-emerald-500/20 text-emerald-400 border border-emerald-500/40 text-xs font-semibold hover:bg-emerald-500/30 transition-colors"
                        >
                          Marcar como Resuelto
                        </button>
                        <button
                          onClick={() => updateDmcaStatus(selectedDmca.id, "rejected", dmcaNote)}
                          className="px-3 py-2 rounded-xl bg-zinc-800 text-zinc-400 border border-zinc-700 text-xs font-semibold hover:bg-zinc-700 transition-colors"
                        >
                          Desestimar
                        </button>
                      </div>

                      <button
                        onClick={() => setSelectedDmca(null)}
                        className="px-4 py-2 rounded-xl bg-zinc-900 border border-zinc-700 text-xs text-zinc-300 hover:text-white"
                      >
                        Cerrar
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </main>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-start justify-center overflow-y-auto py-8 px-4" onClick={() => setEditing(null)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={saveMovie} className="w-full max-w-2xl bg-[#0A0A0C] border border-white/10 p-6 md:p-8" data-testid="movie-form">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading text-xl">{editing.id ? "Editar película" : "Nueva película"}</h2>
              <button type="button" onClick={() => setEditing(null)} className="text-zinc-400 hover:text-white"><X size={20} /></button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                ["title", "Título *", "text", true], ["director", "Dirección", "text"], ["year", "Año", "number"], ["duration", "Duración (1h 30m)", "text"],
                ["trailer_url", "URL del tráiler (YouTube o Vimeo)", "text"],
                ["genres", "Géneros (coma)", "text"], ["cast", "Reparto (coma)", "text"],
              ].map(([key, label, type, req]) => (
                <div key={key} className={key === "title" || key === "description" ? "md:col-span-2" : ""}>
                  <label className="text-xs tracking-wider uppercase text-zinc-400">{label}</label>
                  <input
                    data-testid={`movie-field-${key}`}
                    type={type}
                    required={req}
                    value={Array.isArray(editing[key]) ? editing[key].join(", ") : (editing[key] ?? "")}
                    onChange={(e) => setEditing({ ...editing, [key]: e.target.value })}
                    className="w-full bg-transparent border-b border-white/20 py-2 mt-1 outline-none focus:border-[#E11D48]"
                  />
                </div>
              ))}

              {/* Enhanced Image Uploaders with WebP Compression & Local Server Hosting */}
              <div className="md:col-span-2 space-y-4 pt-2">
                <ImageInputUploader
                  label="Póster de la Película / Serie"
                  value={editing.poster_url}
                  onChange={(val) => setEditing({ ...editing, poster_url: val })}
                  preset="poster"
                  aspectRatio="poster"
                  helperText="Sube una foto o introduce una URL (TMDb u otra). Se guardará en formato WebP optimizado en nuestros servidores."
                />

                <ImageInputUploader
                  label="Fondo / Banner Panorámico (Backdrop)"
                  value={editing.backdrop_url}
                  onChange={(val) => setEditing({ ...editing, backdrop_url: val })}
                  preset="backdrop"
                  aspectRatio="banner"
                  helperText="Imagen horizontal para cabeceras y vistas de detalle de la película."
                />
              </div>

              <div className="md:col-span-2">
                <label className="text-xs tracking-wider uppercase text-zinc-400">Descripción</label>
                <textarea data-testid="movie-field-description" value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} rows={3} className="w-full bg-transparent border border-white/15 p-3 mt-1 outline-none focus:border-[#E11D48] resize-none" />
              </div>
              <div className="md:col-span-2">
                <label className="text-xs tracking-wider uppercase text-zinc-400 block mb-2">Categorías</label>
                <div className="flex flex-wrap gap-2">
                  {categories.map((c) => (
                    <button key={c.id} type="button" data-testid={`movie-cat-${c.id}`} onClick={() => toggleCat(c.id)} className={`px-3 py-1.5 text-sm border transition-colors ${editing.category_ids.includes(c.id) ? "border-[#E11D48] text-[#E11D48] bg-[#E11D48]/10" : "border-white/15 text-zinc-400"}`}>
                      {c.name}
                    </button>
                  ))}
                </div>
              </div>

              {/* Streaming Platforms / Dónde Ver Section */}
              <div className="md:col-span-2 border border-white/10 bg-zinc-950/60 p-4 rounded-lg space-y-4">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 pb-3 border-b border-white/10">
                  <div>
                    <span className="text-xs font-bold uppercase tracking-wider text-rose-400 flex items-center gap-1.5">
                      <Tv size={15} /> Dónde Ver / Plataformas Oficiales
                    </span>
                    <p className="text-[11px] text-zinc-400 mt-0.5">
                      Muestra botones oficiales con logotipos (Netflix, Prime, Disney+, Max, etc.) al lado del botón &quot;Ver película&quot;.
                    </p>
                  </div>

                  <button
                    type="button"
                    onClick={handleAutoDetectPlatforms}
                    disabled={autoDetectingPlatforms}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded bg-rose-500/10 hover:bg-rose-500/20 text-rose-300 border border-rose-500/30 text-xs font-medium transition-colors disabled:opacity-50 shrink-0"
                    title="Consultar proveedores en TMDb y JustWatch automáticamente"
                  >
                    {autoDetectingPlatforms ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />}
                    <span>{autoDetectingPlatforms ? "Buscando..." : "Autodetectar en TMDb"}</span>
                  </button>
                </div>

                {/* Quick Add Presets */}
                <div>
                  <label className="text-[11px] font-semibold uppercase tracking-wider text-zinc-400 block mb-2">
                    Agregar plataforma con un clic:
                  </label>
                  <div className="flex flex-wrap gap-1.5">
                    {STREAMING_PLATFORMS.map((p) => {
                      const isAdded = (editing.platforms || []).some(
                        (ep) => ep.name?.toLowerCase().trim() === p.name.toLowerCase().trim()
                      );
                      return (
                        <button
                          key={p.id}
                          type="button"
                          disabled={isAdded}
                          onClick={() => {
                            const current = Array.isArray(editing.platforms) ? [...editing.platforms] : [];
                            current.push({
                              name: p.name,
                              url: p.defaultUrl || "",
                              type: "stream",
                              logo_url: null,
                            });
                            setEditing({ ...editing, platforms: current });
                          }}
                          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border transition-all ${
                            isAdded
                              ? "bg-zinc-800/80 border-white/5 text-zinc-500 cursor-not-allowed opacity-60"
                              : "bg-zinc-900 border-white/15 text-zinc-200 hover:border-rose-400/60 hover:text-white hover:bg-rose-950/20 cursor-pointer"
                          }`}
                        >
                          <PlatformLogo name={p.name} size={14} />
                          <span>{p.name}</span>
                          {isAdded ? <Check size={11} className="text-emerald-400 ml-0.5" /> : <Plus size={11} className="text-rose-400 ml-0.5" />}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Active Platforms List */}
                <div className="space-y-2.5 pt-1">
                  {(!editing.platforms || editing.platforms.length === 0) ? (
                    <div className="p-4 border border-dashed border-white/10 rounded text-center text-zinc-500 text-xs">
                      No hay plataformas asignadas. Haz clic en las etiquetas arriba o en &quot;Autodetectar en TMDb&quot;.
                    </div>
                  ) : (
                    editing.platforms.map((plat, idx) => (
                      <div
                        key={`plat-${idx}`}
                        className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 p-2.5 bg-zinc-900/90 border border-white/10 rounded-md"
                      >
                        <div className="flex items-center gap-2 min-w-[130px] shrink-0">
                          <PlatformLogo name={plat.name} logoUrl={plat.logo_url} size={22} />
                          <input
                            type="text"
                            placeholder="Nombre plataforma"
                            value={plat.name || ""}
                            onChange={(e) => {
                              const updated = [...editing.platforms];
                              updated[idx] = { ...updated[idx], name: e.target.value };
                              setEditing({ ...editing, platforms: updated });
                            }}
                            className="bg-transparent text-xs font-semibold text-white border-b border-transparent focus:border-rose-400 outline-none w-full py-0.5"
                          />
                        </div>

                        {/* Direct Link Input */}
                        <div className="flex-1 min-w-0">
                          <input
                            type="url"
                            placeholder="URL directa (ej: https://www.netflix.com/title/...)"
                            value={plat.url || ""}
                            onChange={(e) => {
                              const updated = [...editing.platforms];
                              updated[idx] = { ...updated[idx], url: e.target.value };
                              setEditing({ ...editing, platforms: updated });
                            }}
                            className="w-full bg-black/50 border border-white/10 rounded px-2.5 py-1 text-xs text-zinc-200 outline-none focus:border-rose-500 placeholder:text-zinc-600"
                          />
                        </div>

                        {/* Type Selector */}
                        <div className="flex items-center gap-2 shrink-0">
                          <select
                            value={plat.type || "stream"}
                            onChange={(e) => {
                              const updated = [...editing.platforms];
                              updated[idx] = { ...updated[idx], type: e.target.value };
                              setEditing({ ...editing, platforms: updated });
                            }}
                            className="bg-black/60 border border-white/10 rounded px-2 py-1 text-[11px] text-zinc-300 outline-none focus:border-rose-500 cursor-pointer"
                          >
                            <option value="stream">Suscripción</option>
                            <option value="rent">Alquiler</option>
                            <option value="buy">Compra</option>
                            <option value="free">Gratis</option>
                          </select>

                          {plat.url && (
                            <a
                              href={plat.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="p-1 text-zinc-400 hover:text-white"
                              title="Probar enlace"
                            >
                              <ExternalLink size={14} />
                            </a>
                          )}

                          <button
                            type="button"
                            onClick={() => {
                              const updated = editing.platforms.filter((_, i) => i !== idx);
                              setEditing({ ...editing, platforms: updated });
                            }}
                            className="p-1 text-zinc-400 hover:text-rose-400 transition-colors"
                            title="Eliminar plataforma"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </div>
                    ))
                  )}

                  <div className="flex justify-end pt-1">
                    <button
                      type="button"
                      onClick={() => {
                        const current = Array.isArray(editing.platforms) ? [...editing.platforms] : [];
                        current.push({ name: "", url: "", type: "stream", logo_url: null });
                        setEditing({ ...editing, platforms: current });
                      }}
                      className="inline-flex items-center gap-1.5 text-xs text-rose-400 hover:text-rose-300 font-medium py-1 px-2 hover:bg-rose-500/10 rounded transition-colors"
                    >
                      <Plus size={13} />
                      <span>Agregar otra plataforma personalizada</span>
                    </button>
                  </div>
                </div>
              </div>

              <div className="md:col-span-2 space-y-3 pt-2 border-t border-white/10">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input data-testid="movie-field-upcoming" type="checkbox" checked={Boolean(editing.upcoming)} onChange={(e) => setEditing({ ...editing, upcoming: e.target.checked })} className="w-4 h-4 accent-amber-500" />
                  <span className="text-sm font-medium text-amber-300">Marcar como Próximo Estreno / Material Próximo</span>
                </label>

                {editing.upcoming && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pl-7 pt-1 pb-2">
                    <div>
                      <label className="text-xs tracking-wider uppercase text-zinc-400">Texto de etiqueta personalizada (opcional)</label>
                      <input
                        data-testid="movie-field-custom-badge"
                        type="text"
                        placeholder="Ej: Próximo estreno, En cines mayo, etc."
                        value={editing.custom_badge || ""}
                        onChange={(e) => setEditing({ ...editing, custom_badge: e.target.value })}
                        className="w-full bg-zinc-900/80 border border-zinc-700 px-3 py-1.5 text-xs text-white rounded outline-none focus:border-amber-400 mt-1"
                      />
                    </div>
                    <div>
                      <label className="text-xs tracking-wider uppercase text-zinc-400">Fecha o periodo estimado de estreno</label>
                      <input
                        data-testid="movie-field-release-date"
                        type="text"
                        placeholder="Ej: 24 Octubre 2026, Invierno 2026"
                        value={editing.release_date || ""}
                        onChange={(e) => setEditing({ ...editing, release_date: e.target.value })}
                        className="w-full bg-zinc-900/80 border border-zinc-700 px-3 py-1.5 text-xs text-white rounded outline-none focus:border-amber-400 mt-1"
                      />
                    </div>
                  </div>
                )}

                <label className="flex items-center gap-3 cursor-pointer">
                  <input data-testid="movie-field-featured" type="checkbox" checked={Boolean(editing.featured)} onChange={(e) => setEditing({ ...editing, featured: e.target.checked })} className="w-4 h-4 accent-[#E11D48]" />
                  <span className="text-sm">Marcar como destacada (aparece en el banner principal)</span>
                </label>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-8">
              <button type="button" onClick={() => setEditing(null)} className="px-5 py-2.5 border border-white/15 hover:bg-white/5 transition-colors">Cancelar</button>
              <button type="submit" data-testid="save-movie-button" className="bg-[#E11D48] text-white px-6 py-2.5 hover:bg-[#BE123C] transition-colors">Guardar</button>
            </div>
          </form>
        </div>
      )}

      {showImportModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-zinc-900 border border-white/15 w-full max-w-xl p-6 rounded-lg shadow-2xl relative">
            <button
              onClick={() => setShowImportModal(false)}
              className="absolute top-4 right-4 text-zinc-400 hover:text-white"
            >
              <X size={20} />
            </button>

            <div className="flex items-center gap-3 mb-2">
              <Database size={22} className="text-[#E11D48]" />
              <h2 className="font-heading text-xl font-bold">Importar Base de Datos / Películas</h2>
            </div>
            <p className="text-xs text-zinc-400 mb-6 leading-relaxed">
              Importa tus películas de tu sitio web online o copia de seguridad en Excel, CSV o JSON para conservar todo tu catálogo sin perder datos.
            </p>

            <div className="space-y-6">
              {/* Opción 1: Archivo */}
              <div className="p-4 bg-zinc-950 border border-white/10 rounded-lg">
                <strong className="text-white text-sm block mb-1 flex items-center gap-2">
                  <FileSpreadsheet size={16} className="text-rose-400" /> OPCIÓN 1: Archivo (Excel / CSV / JSON)
                </strong>
                <p className="text-xs text-zinc-400 mb-3">
                  Sube tu archivo <code className="text-rose-400 bg-white/5 px-1 py-0.5 rounded">.xlsx</code>, <code className="text-rose-400 bg-white/5 px-1 py-0.5 rounded">.csv</code> o <code className="text-rose-400 bg-white/5 px-1 py-0.5 rounded">.json</code>.
                </p>
                <label className="cursor-pointer inline-flex items-center gap-2 bg-[#E11D48] text-white px-4 py-2 text-xs font-semibold hover:bg-[#BE123C] transition-colors rounded">
                  {importingMovies ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                  {importingMovies ? "Procesando archivo..." : "Seleccionar Archivo de Películas"}
                  <input
                    type="file"
                    accept=".xlsx,.xls,.csv,.json,.txt"
                    className="hidden"
                    onChange={importMoviesFromFile}
                    disabled={importingMovies}
                    data-testid="modal-movies-file-input"
                  />
                </label>
              </div>

              {/* Opción 2: URL / Web */}
              <div className="p-4 bg-zinc-950 border border-white/10 rounded-lg">
                <strong className="text-white text-sm block mb-1 flex items-center gap-2">
                  <Globe size={16} className="text-rose-400" /> OPCIÓN 2: URL de Sitio Web u Online API
                </strong>
                <p className="text-xs text-zinc-400 mb-3">
                  Ingresa la URL del sitio web online (ej: <code className="text-rose-400 bg-white/5 px-1 py-0.5 rounded">https://lacajalgbt.com</code>).
                </p>
                <div className="flex gap-2">
                  <input
                    type="url"
                    placeholder="https://lacajalgbt.com/api/movies"
                    value={importUrl}
                    onChange={(e) => setImportUrl(e.target.value)}
                    className="flex-1 bg-zinc-900 border border-white/15 px-3 py-2 text-xs text-white outline-none focus:border-[#E11D48] rounded"
                    data-testid="modal-movies-url-input"
                  />
                  <button
                    onClick={importMoviesFromUrl}
                    disabled={importingMovies || !importUrl.trim()}
                    className="bg-[#E11D48] text-white px-4 py-2 text-xs font-semibold hover:bg-[#BE123C] transition-colors disabled:opacity-50 flex items-center gap-1.5 rounded shrink-0"
                    data-testid="modal-import-url-button"
                  >
                    {importingMovies ? <Loader2 size={14} className="animate-spin" /> : <Globe size={14} />}
                    Importar
                  </button>
                </div>
              </div>
            </div>

            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setShowImportModal(false)}
                className="px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 text-xs font-medium transition-colors rounded"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
